<div class="w-full max-w-6xl mx-auto p-6">
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-red-600"></i>
      <p class="text-red-800">{{ error() }}</p>
    </div>
  }

  @if (isSaleInactive()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2" role="status">
      <i class="pi pi-trash text-red-600"></i>
      <p class="text-red-800 font-medium">Esta venta está inactiva.</p>
    </div>
  }

  @if (isLinkedSubscriptionInactive()) {
    <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-2">
      <i class="pi pi-exclamation-circle text-amber-600"></i>
      <p class="text-amber-900">La suscripción vinculada está inactiva.</p>
    </div>
  }

  @if (isSaleCancelled() && currentSale()?.cancelReason) {
    <div class="bg-rose-50 border border-rose-200 rounded-lg p-4 mb-6 flex items-start gap-3" role="status">
      <i class="pi pi-ban text-rose-600 mt-0.5 shrink-0" aria-hidden="true"></i>
      <div class="min-w-0 flex-1">
        <p class="text-rose-900 font-medium">Motivo de cancelación</p>
        <p class="mt-1 text-sm text-rose-800 whitespace-pre-wrap break-words">{{ currentSale()?.cancelReason }}</p>
      </div>
    </div>
  }

  <div class="relative">
    @if (formDataLoading()) {
      <div
        class="absolute inset-0 z-40 flex items-start justify-center rounded-xl bg-slate-400/25 backdrop-blur-[2px] pt-24"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="flex flex-col items-center gap-3 rounded-2xl border border-slate-300/80 bg-white/95 px-8 py-6 shadow-lg">
          <i class="pi pi-spin pi-spinner text-3xl text-sky-600"></i>
          <p class="text-sm font-semibold text-slate-800">Cargando venta…</p>
        </div>
      </div>
    }

  <form [formGroup]="saleForm" (ngSubmit)="$event.preventDefault()">
    <p-card styleClass="sale-form-card">
      <ng-template pTemplate="header">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3 min-w-0">
              <p-button
                icon="pi pi-arrow-left"
                [rounded]="true"
                [text]="true"
                severity="secondary"
                type="button"
                (onClick)="cancel()"
                pTooltip="Volver al listado"
                tooltipPosition="bottom"
              />
              <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
                <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <i class="pi pi-file-edit"></i>
                </span>
                <span class="truncate">{{ isEditMode() ? 'Editar venta' : 'Nueva venta' }}</span>
              </h2>
            </div>
            @if (editingSaleId(); as saleId) {
              <p-button
                icon="pi pi-refresh"
                [rounded]="true"
                [text]="true"
                severity="secondary"
                type="button"
                (onClick)="refreshSale()"
                [loading]="loading()"
                [disabled]="loading()"
                pTooltip="Actualizar venta"
                tooltipPosition="bottom"
              />
            }
          </div>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-2">
          <div class="flex min-w-0 flex-wrap items-center gap-2">
            <div class="inline-flex min-w-0 items-baseline gap-x-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
              <span class="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Folio</span>
              @if (editingSaleId() == null) {
                <span class="text-sm font-medium font-mono italic text-slate-600">autogenerado</span>
              } @else {
                <span class="text-sm font-semibold tabular-nums tracking-tight text-slate-900">{{ folioDisplayValue() }}</span>
              }
            </div>
            @if (currentSale()?.paymentReference) {
              <button
                type="button"
                class="inline-flex min-w-0 items-baseline gap-x-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 transition hover:border-sky-300 hover:bg-sky-50"
                (click)="copyPaymentReference()"
                pTooltip="Copiar referencia de conciliación"
                tooltipPosition="bottom"
              >
                <span class="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Ref. pago</span>
                <span class="text-sm font-semibold font-mono tracking-tight text-slate-900">{{ currentSale()?.paymentReference }}</span>
                <i class="pi pi-copy text-xs text-slate-400"></i>
              </button>
            }
            <div
              class="inline-flex items-baseline gap-x-1.5 rounded-lg border bg-white px-2.5 py-1.5"
              [ngClass]="
                isSaleCancelled()
                  ? 'border-rose-200 bg-rose-50'
                  : 'border-slate-200'
              "
            >
              <span
                class="text-[11px] font-semibold tracking-wider"
                [ngClass]="isSaleCancelled() ? 'text-rose-600' : 'text-slate-500'"
              >STATUS:</span>
              <span
                class="text-sm font-medium font-mono"
                [ngClass]="isSaleCancelled() ? 'text-rose-700' : 'text-slate-900'"
              >{{ statusDisplayLabel() }}</span>
              <i
                class="pi pi-question-circle cursor-help text-xs shrink-0"
                [ngClass]="isSaleCancelled() ? 'text-rose-400' : 'text-slate-400'"
                [pTooltip]="statusTooltip"
                tooltipPosition="bottom"
              ></i>
            </div>
            @if (linkedSubscriptionId()) {
              <button
                type="button"
                class="inline-flex min-w-0 items-center gap-x-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5 transition hover:border-teal-300 hover:bg-teal-100 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-teal-400"
                (click)="openLinkedSubscription()"
                [pTooltip]="'Abrir suscripción'"
                tooltipPosition="bottom"
              >
                <i class="pi pi-sync text-teal-600 text-xs"></i>
                <span class="text-[11px] font-semibold uppercase tracking-wider text-teal-700">Suscripción</span>
                <span class="text-sm font-semibold tabular-nums tracking-tight text-teal-900">{{ currentSale()?.subscription?.number || '—' }}</span>
              </button>
            }
          </div>

          <div
            class="flex flex-wrap items-center justify-end gap-1.5"
            role="toolbar"
            aria-label="Enlaces fiscales y de cobro"
          >
            @if (editingSaleId()) {
              @if (currentSale(); as saleRow) {
                @if (!saleRow.invoice) {
                  <button
                    type="button"
                    class="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200/80 bg-white px-2 text-xs font-medium text-slate-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sky-400 disabled:pointer-events-none disabled:opacity-40"
                    (click)="confirmStampSale()"
                    [disabled]="loading() || stampSaleLoading() || !canStampSaleCfdi()"
                    appDisableUnless="SALE_WRITE"
                    [pTooltip]="
                      canStampSaleCfdi()
                        ? 'Timbrar CFDI de esta venta'
                        : 'Solo disponible si la venta está emitida, parcialmente pagada o pagada'
                    "
                    tooltipPosition="bottom"
                  >
                    <i class="pi text-sky-600 text-sm" [ngClass]="stampSaleLoading() ? 'pi pi-spinner pi-spin' : 'pi pi-file-export'"></i>
                    <span class="hidden sm:inline">Facturar</span>
                  </button>
                } @else {
                  <span
                    class="inline-flex h-8 items-center [&_.p-splitbutton]:flex [&_.p-splitbutton]:h-8 [&_.p-splitbutton]:overflow-hidden [&_.p-splitbutton]:rounded-md [&_.p-splitbutton]:border [&_.p-splitbutton]:border-slate-200/80 [&_.p-splitbutton]:bg-white [&_.p-splitbutton]:shadow-sm [&_.p-splitbutton-button]:!rounded-none [&_.p-splitbutton-button]:!border-0 [&_.p-splitbutton-button]:!border-r [&_.p-splitbutton-button]:!border-slate-200/80 [&_.p-splitbutton-button]:!bg-white [&_.p-splitbutton-button]:!text-xs [&_.p-splitbutton-button]:!text-slate-700 [&_.p-splitbutton-button]:!shadow-none [&_.p-splitbutton-button:not(:disabled):hover]:!bg-sky-50 [&_.p-splitbutton-dropdown]:!rounded-none [&_.p-splitbutton-dropdown]:!border-0 [&_.p-splitbutton-dropdown]:!bg-white [&_.p-splitbutton-dropdown]:!text-slate-700 [&_.p-splitbutton-dropdown]:!shadow-none [&_.p-splitbutton-dropdown]:!w-9 [&_.p-splitbutton-dropdown]:min-w-0 [&_.p-splitbutton-dropdown:not(:disabled):hover]:!bg-sky-50 [&_.p-splitbutton_.p-button]:!py-1 [&_.p-splitbutton-button_.p-button-icon]:!text-sm [&_.p-splitbutton-button_.p-button-icon]:!text-sky-600 [&_.p-splitbutton-button_.p-button-loading-icon]:!text-sm [&_.p-splitbutton-button_.p-button-loading-icon]:!text-sky-600 [&_.p-splitbutton-button_.p-button-loading-icon_svg]:!h-4 [&_.p-splitbutton-button_.p-button-loading-icon_svg]:!w-4 [&_.p-splitbutton-button_.p-button-loading-icon_svg]:!text-sky-600 [&_.p-splitbutton-dropdown]:!text-sky-600 [&_.p-splitbutton-dropdown_svg]:!h-4 [&_.p-splitbutton-dropdown_svg]:!w-4 [&_.p-splitbutton-dropdown_svg]:fill-current"
                    pTooltip="Ver PDF del CFDI (clic). Descargar PDF o XML desde el menú."
                    tooltipPosition="bottom"
                  >
                    <p-splitbutton
                      label="CFDI"
                      icon="pi pi-eye"
                      severity="secondary"
                      [outlined]="true"
                      size="small"
                      [model]="saleStampSplitMenuItems()"
                      (onClick)="openCfdiPdfViewer()"
                      [disabled]="loading() || cfdiPdfViewerOpening() || downloadPdfLoading() || downloadXmlLoading() || resendCfdiLoading() || cancelCfdiLoading()"
                      [loading]="cfdiPdfViewerOpening()"
                      type="button"
                    />
                  </span>
                }
              }

              <span
                class="inline-flex h-8 items-center [&_.p-splitbutton]:flex [&_.p-splitbutton]:h-8 [&_.p-splitbutton]:overflow-hidden [&_.p-splitbutton]:rounded-md [&_.p-splitbutton]:border [&_.p-splitbutton]:border-slate-200/80 [&_.p-splitbutton]:bg-white [&_.p-splitbutton]:shadow-sm [&_.p-splitbutton-button]:!rounded-none [&_.p-splitbutton-button]:!border-0 [&_.p-splitbutton-button]:!border-r [&_.p-splitbutton-button]:!border-slate-200/80 [&_.p-splitbutton-button]:!bg-white [&_.p-splitbutton-button]:!text-xs [&_.p-splitbutton-button]:!text-slate-700 [&_.p-splitbutton-button]:!shadow-none [&_.p-splitbutton-button:not(:disabled):hover]:!bg-emerald-50 [&_.p-splitbutton-dropdown]:!rounded-none [&_.p-splitbutton-dropdown]:!border-0 [&_.p-splitbutton-dropdown]:!bg-white [&_.p-splitbutton-dropdown]:!text-slate-700 [&_.p-splitbutton-dropdown]:!shadow-none [&_.p-splitbutton-dropdown]:!w-9 [&_.p-splitbutton-dropdown]:min-w-0 [&_.p-splitbutton-dropdown:not(:disabled):hover]:!bg-emerald-50 [&_.p-splitbutton_.p-button]:!py-1 [&_.p-splitbutton-button_.p-button-icon]:!text-sm [&_.p-splitbutton-button_.p-button-icon]:!text-emerald-600 [&_.p-splitbutton-button_.p-button-loading-icon]:!text-sm [&_.p-splitbutton-button_.p-button-loading-icon]:!text-emerald-600 [&_.p-splitbutton-button_.p-button-loading-icon_svg]:!h-4 [&_.p-splitbutton-button_.p-button-loading-icon_svg]:!w-4 [&_.p-splitbutton-button_.p-button-loading-icon_svg]:!text-emerald-600 [&_.p-splitbutton-dropdown]:!text-emerald-600 [&_.p-splitbutton-dropdown_svg]:!h-4 [&_.p-splitbutton-dropdown_svg]:!w-4 [&_.p-splitbutton-dropdown_svg]:fill-current"
                pTooltip="Ir al recibo de pago del cliente."
                tooltipPosition="bottom"
              >
                <p-splitbutton
                  label="Ver Recibo"
                  icon="pi pi-receipt"
                  severity="secondary"
                  [outlined]="true"
                  size="small"
                  [model]="cobroSplitMenuItems()"
                  (onClick)="openFirstReceiptShortcut()"
                  [disabled]="loading()"
                  [loading]="chargesLoading() || receiptLinkCopyLoading()"
                  type="button"
                />
              </span>

              @if (!isSaleCancelled() && !isSaleInactive() && !isMerchantInitiatedSale()) {
                <button
                  type="button"
                  class="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200/80 bg-white px-2 text-xs font-medium text-slate-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-50 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-rose-400 disabled:pointer-events-none disabled:opacity-40"
                  (click)="confirmCancelSale()"
                  [disabled]="loading() || cancelSaleLoading()"
                  appDisableUnless="SALE_WRITE"
                  pTooltip="Cancelar venta"
                  tooltipPosition="bottom"
                >
                  <i
                    class="pi text-rose-600 text-sm"
                    [ngClass]="cancelSaleLoading() ? 'pi-spinner pi-spin' : 'pi-ban'"
                  ></i>
                  <span class="hidden sm:inline">Cancelar venta</span>
                </button>
              }
              @if (canDeleteSale()) {
                <button
                  type="button"
                  class="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200/80 bg-white px-2 text-xs font-medium text-slate-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-50 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-rose-400 disabled:pointer-events-none disabled:opacity-40"
                  (click)="confirmDeleteSale()"
                  [disabled]="loading() || deleteSaleLoading()"
                  appDisableUnless="SALE_WRITE"
                  pTooltip="Eliminar venta cancelada"
                  tooltipPosition="bottom"
                >
                  <i
                    class="pi text-rose-600 text-sm"
                    [ngClass]="deleteSaleLoading() ? 'pi-spinner pi-spin' : 'pi-trash'"
                  ></i>
                  <span class="hidden sm:inline">Eliminar</span>
                </button>
              }
            }
          </div>
        </div>
      </ng-template>
      <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
        <p-tablist>
          <p-tab value="general" class="flex items-center gap-2! text-inherit">
            <i class="pi pi-receipt"></i>
            <span>General</span>
          </p-tab>
          <p-tab
            value="charges"
            [disabled]="!editingSaleId()"
            class="flex items-center gap-2! text-inherit"
            [class.opacity-50]="!editingSaleId()"
            [class.pointer-events-none]="!editingSaleId()"
          >
            <i class="pi pi-credit-card"></i>
            <span>Recibos de pago</span>
          </p-tab>
          <p-tab
            value="cfdis"
            [disabled]="!editingSaleId()"
            class="flex items-center gap-2! text-inherit"
            [class.opacity-50]="!editingSaleId()"
            [class.pointer-events-none]="!editingSaleId()"
          >
            <i class="pi pi-file"></i>
            <span>CFDI's</span>
          </p-tab>
          <p-tab
            value="attachments"
            [disabled]="!editingSaleId()"
            class="flex items-center gap-2! text-inherit"
            [class.opacity-50]="!editingSaleId()"
            [class.pointer-events-none]="!editingSaleId()"
          >
            <i class="pi pi-paperclip"></i>
            <span>Adjuntos</span>
          </p-tab>
          <p-tab
            value="auditoria"
            [disabled]="!editingSaleId()"
            class="flex items-center gap-2! text-inherit"
            [class.opacity-50]="!editingSaleId()"
            [class.pointer-events-none]="!editingSaleId()"
          >
            <i class="pi pi-history"></i>
            <span>Auditoría</span>
          </p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="general">
            <div formGroupName="general">
            <div class="hidden" aria-hidden="true">
              <input type="text" formControlName="number" maxlength="20" tabindex="-1" />
            </div>
            <!-- Datos de la venta -->
            <div class="mb-8">
              <h3 class="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i class="pi pi-file-edit"></i>
                Datos de la venta
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-1 md:col-span-2">
                  <div class="flex items-start gap-2">
                    <p-floatlabel class="w-full min-w-0 flex-1" variant="on">
                      <p-autocomplete
                        id="customer"
                        formControlName="customerSelection"
                        [suggestions]="customerOptions()"
                        (completeMethod)="onCustomerSearch($event)"
                        (onSelect)="onCustomerSelected($event.value)"
                        optionLabel="displayLabel"
                        placeholder="Busca por nombre comercial o legal (mín. 2 caracteres)"
                        [minLength]="2"
                        fluid
                        [disabled]="isFieldReadonly(SaleEditField.CUSTOMER_ID)"
                        [pTooltip]="fieldReadonlyTooltip(SaleEditField.CUSTOMER_ID)"
                        tooltipPosition="top"
                        [invalid]="(generalForm.get('customerId')?.invalid ?? false) && (generalForm.get('customerId')?.touched ?? false)"
                      >
                        <ng-template #item let-customer>
                          <div class="flex flex-col py-0.5">
                            <span class="text-sm font-medium text-slate-900 leading-snug">{{ customer.comercialName }}</span>
                            <span class="text-xs text-slate-400 leading-snug">{{ customer.legalName }}</span>
                          </div>
                        </ng-template>
                      </p-autocomplete>
                      <label for="customer">Cliente <span class="text-red-500">*</span></label>
                    </p-floatlabel>
                    <p-button
                      type="button"
                      icon="pi pi-eye"
                      severity="secondary"
                      [outlined]="true"
                      [disabled]="!generalForm.get('customerId')?.value"
                      pTooltip="Ver cliente"
                      tooltipPosition="top"
                      styleClass="shrink-0"
                      (onClick)="openCustomerDetails()" />
                  </div>
                  <small class="text-slate-500">Busca por nombre comercial o legal.</small>
                  @if (isFieldReadonly(SaleEditField.CUSTOMER_ID)) {
                    <small class="text-slate-500">{{ fieldReadonlyTooltip(SaleEditField.CUSTOMER_ID) }}</small>
                  }
                  @if (generalForm.get('customerId')?.hasError('required') && generalForm.get('customerId')?.touched) {
                    <small class="text-red-600">El cliente es obligatorio.</small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <div class="flex w-full min-w-0 items-center gap-2">
                    <p-floatlabel class="min-w-0 flex-1" variant="on">
                      <p-datepicker
                        formControlName="dateOfSale"
                        inputId="dateOfSale"
                        [showTime]="false"
                        dateFormat="dd/mm/yy"
                        placeholder="dd/mm/aaaa"
                        fluid
                        [disabled]="isFieldReadonly(SaleEditField.DATE_OF_SALE)"
                        [pTooltip]="fieldReadonlyTooltip(SaleEditField.DATE_OF_SALE)"
                        tooltipPosition="top"
                      />
                      <label for="dateOfSale">Fecha de venta</label>
                    </p-floatlabel>
                    <ng-template #dateOfSaleTooltipGeneralTab>
                      <p>Fecha comercial en que se cerró o registró la venta. Por defecto es el día actual.</p>
                    </ng-template>
                    <i
                      class="pi pi-info-circle text-slate-400 cursor-help shrink-0"
                      [pTooltip]="dateOfSaleTooltipGeneralTab"
                      tooltipPosition="left"
                    ></i>
                  </div>
                  <small class="text-slate-500">Fecha en que se realizó la venta.</small>
                  @if (isFieldReadonly(SaleEditField.DATE_OF_SALE)) {
                    <small class="text-slate-500">{{ fieldReadonlyTooltip(SaleEditField.DATE_OF_SALE) }}</small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <div class="relative w-full">
                      <p-select
                        formControlName="collectionMethod"
                        inputId="collectionMethod"
                        [options]="collectionMethodOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [disabled]="isFieldReadonly(SaleEditField.COLLECTION_METHOD)"
                        [pTooltip]="fieldReadonlyTooltip(SaleEditField.COLLECTION_METHOD)"
                        tooltipPosition="top"
                        [invalid]="(generalForm.get('collectionMethod')?.invalid ?? false) && (generalForm.get('collectionMethod')?.touched ?? false)"
                        class="w-full pr-9"
                      />
                      <ng-template #collectionMethodTooltipGeneralTab>
                        <ul>
                          <li><strong>Automático</strong>: el cobro se realiza de forma automática según la configuración.<br/><br/></li>
                          <li><strong>Link de pago</strong>: genera un enlace de pago para que el cliente pague cuando lo desee.<br/><br/></li>
                          <li><strong>Manual</strong>: el cobro se gestiona manualmente fuera del sistema.<br/><br/></li>
                        </ul>
                      </ng-template>
                      <i
                        class="pi pi-info-circle text-slate-400 cursor-help absolute right-2 top-1/2 -translate-y-1/2 pointer-events-auto"
                        [pTooltip]="collectionMethodTooltipGeneralTab"
                        tooltipPosition="left"
                      ></i>
                    </div>
                    <label for="collectionMethod">Método de cobro <span class="text-red-500">*</span></label>
                  </p-floatlabel>
                  @if (generalForm.get('collectionMethod')?.hasError('required') && generalForm.get('collectionMethod')?.touched) {
                    <small class="text-red-600">El método de cobro es obligatorio.</small>
                    } @else {
                    <small class="text-slate-500">Selecciona el método de cobro para la factura.</small>
                    @if (isFieldReadonly(SaleEditField.COLLECTION_METHOD)) {
                      <small class="text-slate-500 block">{{ fieldReadonlyTooltip(SaleEditField.COLLECTION_METHOD) }}</small>
                    }
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="currencyId"
                      inputId="currencyId"
                      [options]="currencySelectOptions()"
                      optionLabel="label"
                      optionValue="id"
                      class="w-full"
                      filter
                      [disabled]="isFieldReadonly(SaleEditField.CURRENCY_ID)"
                      [pTooltip]="fieldReadonlyTooltip(SaleEditField.CURRENCY_ID)"
                      tooltipPosition="top"
                      [invalid]="(generalForm.get('currencyId')?.invalid ?? false) && (generalForm.get('currencyId')?.touched ?? false)"
                    />
                    <label for="currencyId">Moneda <span class="text-red-500">*</span></label>
                  </p-floatlabel>
                  @if (generalForm.get('currencyId')?.hasError('required') && generalForm.get('currencyId')?.touched) {
                    <small class="text-red-600">La moneda es obligatoria.</small>
                  } @else {
                    <small class="text-slate-500">La moneda se usará para calcular los totales de la venta.</small>
                    @if (isFieldReadonly(SaleEditField.CURRENCY_ID)) {
                      <small class="text-slate-500 block">{{ fieldReadonlyTooltip(SaleEditField.CURRENCY_ID) }}</small>
                    }
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="processStatus"
                      inputId="generalProcessStatus"
                      [options]="salePipelineStatusOptions"
                      optionLabel="label"
                      optionValue="value"
                      class="w-full"
                      [disabled]="isFieldReadonly(SaleEditField.PROCESS_STATUS)"
                      [pTooltip]="fieldReadonlyTooltip(SaleEditField.PROCESS_STATUS)"
                      tooltipPosition="top"
                      [invalid]="(generalForm.get('processStatus')?.invalid ?? false) && (generalForm.get('processStatus')?.touched ?? false)"
                    />
                    <label for="generalProcessStatus">Status de la venta <span class="text-red-500">*</span></label>
                  </p-floatlabel>
                  <small class="text-slate-500">Etapa comercial del embudo (aceptado, contrato/anexo, OC, facturado, etc.).</small>
                </div>

                <div class="hidden" aria-hidden="true">
                  <input type="hidden" formControlName="status" />
                </div>

                <div class="flex flex-col  md:col-span-2">
                  <p-floatlabel class="w-full" variant="on">
                    <textarea
                      pTextarea
                      id="notes"
                      formControlName="notes"
                      rows="3"
                      [maxlength]="500"
                      fluid
                      autocomplete="off"
                      placeholder="Notas adicionales"
                      [disabled]="isFieldReadonly(SaleEditField.NOTES)"
                      [readonly]="isFieldReadonly(SaleEditField.NOTES)"
                      [pTooltip]="fieldReadonlyTooltip(SaleEditField.NOTES)"
                      tooltipPosition="top"
                    ></textarea>
                    <label for="notes">Notas</label>
                  </p-floatlabel>
                  @if (generalForm.get('notes')?.hasError('maxlength')) {
                    <small class="text-red-600">Máximo 500 caracteres.</small>
                  } @else {
                    <small class="text-slate-500">Las notas de uso interno.</small>
                  }
                </div>
              </div>
            </div>

            <!-- Términos de pago -->
            <div class="mt-8 border-t border-slate-200 pt-6" [formGroup]="paymentTermsForm">
              <div class="mb-4">
                <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <i class="pi pi-wallet"></i>
                  Términos de pago
                </h3>
                <p class="text-sm text-slate-500">Define cuotas, periodicidad y plazos asociados a la venta.</p>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-1">
                  @if (isSubscriptionSaleType()) {
                    <p-floatlabel class="w-full" variant="on">
                      <input
                        type="text"
                        pInputText
                        readonly
                        disabled
                        id="numberOfPaymentsInfinity"
                        class="w-full text-left italic tracking-wide text-slate-800"
                        value="Hasta la cancelación de la suscripción"
                        [tabIndex]="isFieldReadonly(SaleEditField.NUMBER_OF_PAYMENTS) ? -1 : 0"
                      />
                      <label for="numberOfPaymentsInfinity">Número de pagos</label>
                    </p-floatlabel>
                    <small class="text-slate-500">Los cobros continúan hasta cancelar la suscripción.</small>
                    @if (isFieldReadonly(SaleEditField.NUMBER_OF_PAYMENTS)) {
                      <small class="text-slate-500">{{ fieldReadonlyTooltip(SaleEditField.NUMBER_OF_PAYMENTS) }}</small>
                    }
                  } @else {
                    <p-floatlabel class="w-full" variant="on">
                      <p-inputNumber
                        id="numberOfPayments"
                        formControlName="numberOfPayments"
                        [min]="1"
                        [max]="1"
                        mode="decimal"
                        [minFractionDigits]="0"
                        [maxFractionDigits]="0"
                        fluid
                        [disabled]="true"
                        [readonly]="true"
                        pTooltip="Las ventas normales se cobran en un solo pago."
                        tooltipPosition="top"
                      />
                      <label for="numberOfPayments">Número de pagos</label>
                    </p-floatlabel>
                    <small class="text-slate-500">Un solo pago. Las cuotas múltiples aún no están disponibles.</small>
                  }
                </div>
                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="frequency"
                      inputId="frequency"
                      [options]="frequencySelectOptions()"
                      optionLabel="label"
                      optionValue="value"
                      optionDisabled="disabled"
                      class="w-full"
                      placeholder="Frecuencia"
                      [disabled]="isFieldReadonly(SaleEditField.FREQUENCY)"
                      [pTooltip]="fieldReadonlyTooltip(SaleEditField.FREQUENCY)"
                      tooltipPosition="top"
                    />
                    <label for="frequency">Frecuencia</label>
                  </p-floatlabel>
                  <small class="text-slate-500">
                    @if (isSubscriptionSaleType()) {
                      Periodicidad de los pagos (mensual, trimestral, etc.).
                    } @else {
                      Periodicidad de los pagos (una sola vez, mensual, trimestral, etc.).
                    }
                  </small>
                  @if (isFieldReadonly(SaleEditField.FREQUENCY)) {
                    <small class="text-slate-500">{{ fieldReadonlyTooltip(SaleEditField.FREQUENCY) }}</small>
                  }
                </div>
                <div class="flex flex-col gap-1">
                  <div class="flex w-full min-w-0 items-center gap-2">
                    <p-floatlabel class="min-w-0 flex-1" variant="on">
                      <p-datepicker
                        formControlName="collectionStartDate"
                        inputId="collectionStartDate"
                        [showTime]="false"
                        dateFormat="dd/mm/yy"
                        placeholder="dd/mm/aaaa"
                        fluid
                        [disabled]="isFieldReadonly(SaleEditField.COLLECTION_START_DATE)"
                        [pTooltip]="fieldReadonlyTooltip(SaleEditField.COLLECTION_START_DATE)"
                        tooltipPosition="top"
                      />
                      <label for="collectionStartDate">Fecha inicio de cobro</label>
                    </p-floatlabel>
                    <ng-template #collectionStartDateTooltipPaymentTab>
                      <p>Fecha a partir de la cual se programan o inician los cobros de esta venta (primera cuota o primer intento de cobro según la frecuencia y el método de cobro). <br/><br/>Si se deja en blanco se toma la fecha actual al mometo de cerrar la venta.</p>
                    </ng-template>
                    <i
                      class="pi pi-info-circle text-slate-400 cursor-help shrink-0"
                      [pTooltip]="collectionStartDateTooltipPaymentTab"
                      tooltipPosition="left"
                    ></i>
                  </div>
                  <small class="text-slate-500">Fecha en la que iniciarán los cobros.</small>
                  @if (isFieldReadonly(SaleEditField.COLLECTION_START_DATE)) {
                    <small class="text-slate-500">{{ fieldReadonlyTooltip(SaleEditField.COLLECTION_START_DATE) }}</small>
                  }
                </div>
                <div class="flex flex-col gap-1">
                  <div class="flex w-full min-w-0 items-center gap-2">
                    <p-floatlabel class="min-w-0 flex-1" variant="on">
                      <p-inputNumber
                        id="daysUntilExpiry"
                        formControlName="daysUntilExpiry"
                        [min]="0"
                        mode="decimal"
                        [minFractionDigits]="0"
                        [maxFractionDigits]="0"
                        fluid
                        placeholder="0"
                        [disabled]="isFieldReadonly(SaleEditField.DAYS_UNTIL_EXPIRY)"
                        [pTooltip]="fieldReadonlyTooltip(SaleEditField.DAYS_UNTIL_EXPIRY)"
                        tooltipPosition="top"
                        class="w-full"
                      />
                      <label for="daysUntilExpiry">Plazo de pago</label>
                    </p-floatlabel>
                    <ng-template #daysUntilExpiryTooltipPaymentTab>
                      <p>Este campo indica el número de días que el cliente tiene para pagar la factura antes de que expire la vigencia del pago y sea considerado vencido. Si no se establece, la factura se considera vencida al día siguiente de la fecha de inicio de cobro.</p>
                    </ng-template>
                    <i
                      class="pi pi-info-circle text-slate-400 cursor-help shrink-0"
                      [pTooltip]="daysUntilExpiryTooltipPaymentTab"
                      tooltipPosition="left"
                    ></i>
                  </div>
                  <small class="text-slate-500 items-center flex gap-1"><i class="pi pi-wave-pulse text-amber-500 !text-sm" pTooltip="Este campo permite disparar un webhook o notificación para hacer una acción en consecuencia."></i> Días que tiene el cliente para pagar antes de vencer (<a href="#" target="_blank" class="text-sky-600 hover:text-sky-800">Leer más</a>).</small>
                  @if (isFieldReadonly(SaleEditField.DAYS_UNTIL_EXPIRY)) {
                    <small class="text-slate-500">{{ fieldReadonlyTooltip(SaleEditField.DAYS_UNTIL_EXPIRY) }}</small>
                  }
                </div>
              </div>
            </div>

            <!-- Facturación -->
            <div class="mt-8 border-t border-slate-200 pt-6" [formGroup]="invoicingForm">
              <div class="mb-4">
                <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <i class="pi pi-file-check"></i>
                  Facturación
                </h3>
                <p class="text-sm text-slate-500">Datos fiscales opcionales (CFDI).</p>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="paymentMethodId"
                      inputId="paymentMethodIdInvoicing"
                      [options]="paymentMethodOptions()"
                      optionLabel="description"
                      optionValue="id"
                      class="w-full"
                      filter
                      [disabled]="isFieldReadonly(SaleEditField.PAYMENT_METHOD_ID)"
                      [pTooltip]="fieldReadonlyTooltip(SaleEditField.PAYMENT_METHOD_ID)"
                      tooltipPosition="top"
                      [invalid]="(invoicingForm.get('paymentMethodId')?.invalid ?? false) && (invoicingForm.get('paymentMethodId')?.touched ?? false)"
                    />
                    <label for="paymentMethodIdInvoicing">Método de pago @if (requiresAutoInvoiceFiscalFields()) {<span class="text-red-500">*</span>}</label>
                  </p-floatlabel>
                  @if (invoicingForm.get('paymentMethodId')?.hasError('required') && invoicingForm.get('paymentMethodId')?.touched) {
                    <small class="text-red-600">El método de pago es obligatorio.</small>
                  } @else {
                    <small class="text-slate-500">El método de utilizado para crear el comprobante CFDI.</small>
                    @if (isFieldReadonly(SaleEditField.PAYMENT_METHOD_ID)) {
                      <small class="text-slate-500 block">{{ fieldReadonlyTooltip(SaleEditField.PAYMENT_METHOD_ID) }}</small>
                    }
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="paymentFormId"
                      inputId="paymentFormIdInvoicing"
                      [options]="paymentFormOptions()"
                      optionLabel="description"
                      optionValue="id"
                      class="w-full"
                      filter
                      [showClear]="true"
                      [disabled]="isFieldReadonly(SaleEditField.PAYMENT_FORM_ID)"
                      [pTooltip]="fieldReadonlyTooltip(SaleEditField.PAYMENT_FORM_ID)"
                      tooltipPosition="top"
                      [invalid]="(invoicingForm.get('paymentFormId')?.invalid ?? false) && (invoicingForm.get('paymentFormId')?.touched ?? false)"
                    />
                    <label for="paymentFormIdInvoicing">Forma de pago @if (isFiscalBillingType()) {<span class="text-red-500">*</span>}</label>
                  </p-floatlabel>
                  @if (invoicingForm.get('paymentFormId')?.hasError('required') && invoicingForm.get('paymentFormId')?.touched) {
                    <small class="text-red-600">La forma de pago es obligatoria.</small>
                  } @else {
                    <small class="text-slate-500">La forma de pago utilizada para crear el comprobante CFDI.</small>
                    @if (isFieldReadonly(SaleEditField.PAYMENT_FORM_ID)) {
                      <small class="text-slate-500 block">{{ fieldReadonlyTooltip(SaleEditField.PAYMENT_FORM_ID) }}</small>
                    }
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="cfdiUseId"
                      inputId="cfdiUseIdInvoicing"
                      [options]="satCfdiUseOptions()"
                      optionLabel="description"
                      optionValue="id"
                      class="w-full"
                      filter
                      [showClear]="true"
                      [disabled]="isFieldReadonly(SaleEditField.CFDI_USE_ID)"
                      [pTooltip]="fieldReadonlyTooltip(SaleEditField.CFDI_USE_ID)"
                      tooltipPosition="top"
                      [invalid]="(invoicingForm.get('cfdiUseId')?.invalid ?? false) && (invoicingForm.get('cfdiUseId')?.touched ?? false)"
                    />
                    <label for="cfdiUseIdInvoicing">Uso de CFDI @if (requiresAutoInvoiceFiscalFields()) {<span class="text-red-500">*</span>}</label>
                  </p-floatlabel>
                  @if (invoicingForm.get('cfdiUseId')?.hasError('required') && invoicingForm.get('cfdiUseId')?.touched) {
                    <small class="text-red-600">El uso de CFDI es obligatorio.</small>
                  } @else {
                    <small class="text-slate-500">Requerido para facturas con valor fiscal (CFDI).</small>
                    @if (isFieldReadonly(SaleEditField.CFDI_USE_ID)) {
                      <small class="text-slate-500 block">{{ fieldReadonlyTooltip(SaleEditField.CFDI_USE_ID) }}</small>
                    }
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-inputgroup class="w-full">
                    <div
                      class="flex items-center gap-3 flex-1 min-h-[2.75rem] px-3 border border-slate-300 rounded-md"
                      [class.bg-white]="canEditInvoicingPaymentAndCfdiFields()"
                      [class.bg-slate-100]="!canEditInvoicingPaymentAndCfdiFields()"
                      [pTooltip]="fieldReadonlyTooltip(SaleEditField.AUTO_INVOICE)"
                      tooltipPosition="top"
                    >
                      <p-checkbox
                        formControlName="autoInvoice"
                        inputId="autoInvoiceSale"
                        [binary]="true"
                        [disabled]="isFieldReadonly(SaleEditField.AUTO_INVOICE)"
                      />
                      <label
                        for="autoInvoiceSale"
                        class="select-none text-sm text-slate-700"
                        [class.cursor-not-allowed]="isFieldReadonly(SaleEditField.AUTO_INVOICE)"
                        [class.cursor-pointer]="!isFieldReadonly(SaleEditField.AUTO_INVOICE)"
                      >
                        Generar factura automáticamente
                      </label>
                      <i
                        class="pi pi-info-circle text-slate-400 cursor-help ml-auto shrink-0"
                        pTooltip="Seleccionar esta opción provocará que se genere la factura de una venta automáticamente después de recibir el pago."
                        tooltipPosition="top"
                      ></i>
                    </div>
                  </p-inputgroup>
                  @if (autoInvoiceCustomerWarning(); as warning) {
                    <small class="text-red-600">{{ warning }}</small>
                  }
                  @if (autoInvoiceProductWarning(); as productWarning) {
                    <small class="text-red-600">{{ productWarning }}</small>
                  }
                </div>
              </div>
            </div>

            @if (dynamicFields().length) {
              <!-- Campos adicionales -->
              <div class="mt-8 border-t border-slate-200 pt-6" [formGroup]="additionalFieldsForm">
                <div class="mb-4">
                  <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <i class="pi pi-sliders-h"></i>
                    Campos adicionales
                  </h3>
                  <p class="text-sm text-slate-500">Datos complementarios configurados para tu empresa.</p>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  @for (field of dynamicFields(); track field.fieldKey) {
                    <div class="flex flex-col gap-1">
                      @if (field.type === 'BOOLEAN') {
                        <p-inputgroup class="w-full">
                          <div
                            class="flex items-center gap-3 flex-1 min-h-[2.75rem] px-3 border border-slate-300 rounded-l-md"
                            [class.bg-white]="canEditAdditionalFields()"
                            [class.bg-slate-100]="!canEditAdditionalFields()"
                          >
                            <p-checkbox
                              [formControlName]="field.fieldKey!"
                              [binary]="true"
                              [inputId]="'dyn_' + field.fieldKey"
                            />
                            <label [for]="'dyn_' + field.fieldKey" class="text-sm text-slate-700">
                              {{ field.label | titlecase }}
                              @if (field.required) {
                                <span class="text-red-500">*</span>
                              }
                            </label>
                          </div>
                          <p-inputgroup-addon>{{ fieldTypeLabel(field.type) }}</p-inputgroup-addon>
                        </p-inputgroup>
                      } @else if (field.type === 'NUMBER') {
                        <p-floatlabel class="w-full dyn-field-floatlabel" variant="on">
                          <p-inputgroup class="w-full">
                            <p-inputnumber
                              [formControlName]="field.fieldKey!"
                              [inputId]="'dyn_' + field.fieldKey"
                              class="w-full"
                              styleClass="w-full"
                              [useGrouping]="false"
                              [invalid]="(additionalFieldCtrl(field.fieldKey)?.invalid ?? false) && (additionalFieldCtrl(field.fieldKey)?.touched ?? false)"
                            />
                            <p-inputgroup-addon>{{ fieldTypeLabel(field.type) }}</p-inputgroup-addon>
                          </p-inputgroup>
                          <label [for]="'dyn_' + field.fieldKey">
                            {{ field.label | titlecase }}
                            @if (field.required) {
                              <span class="text-red-500">*</span>
                            }
                          </label>
                        </p-floatlabel>
                      } @else if (field.type === 'DATE') {
                        <p-floatlabel class="w-full dyn-field-floatlabel" variant="on">
                          <p-inputgroup class="w-full">
                            <p-datepicker
                              [formControlName]="field.fieldKey!"
                              [inputId]="'dyn_' + field.fieldKey"
                              dateFormat="dd/mm/yy"
                              [showIcon]="true"
                              class="w-full"
                              styleClass="w-full"
                              appendTo="body"
                              [invalid]="(additionalFieldCtrl(field.fieldKey)?.invalid ?? false) && (additionalFieldCtrl(field.fieldKey)?.touched ?? false)"
                            />
                            <p-inputgroup-addon>{{ fieldTypeLabel(field.type) }}</p-inputgroup-addon>
                          </p-inputgroup>
                          <label [for]="'dyn_' + field.fieldKey">
                            {{ field.label | titlecase }}
                            @if (field.required) {
                              <span class="text-red-500">*</span>
                            }
                          </label>
                        </p-floatlabel>
                      } @else if (field.type === 'SELECT') {
                        <p-floatlabel class="w-full dyn-field-floatlabel" variant="on">
                          <p-inputgroup class="w-full">
                            <p-select
                              [formControlName]="field.fieldKey!"
                              [inputId]="'dyn_' + field.fieldKey"
                              [options]="selectOptionsForField(field)"
                              optionLabel="label"
                              optionValue="value"
                              class="w-full"
                              [invalid]="(additionalFieldCtrl(field.fieldKey)?.invalid ?? false) && (additionalFieldCtrl(field.fieldKey)?.touched ?? false)"
                            />
                            <p-inputgroup-addon>{{ fieldTypeLabel(field.type) }}</p-inputgroup-addon>
                          </p-inputgroup>
                          <label [for]="'dyn_' + field.fieldKey">
                            {{ field.label | titlecase }}
                            @if (field.required) {
                              <span class="text-red-500">*</span>
                            }
                          </label>
                        </p-floatlabel>
                      } @else {
                        <p-floatlabel class="w-full dyn-field-floatlabel" variant="on">
                          <p-inputgroup class="w-full">
                            <input
                              pInputText
                              [formControlName]="field.fieldKey!"
                              [id]="'dyn_' + field.fieldKey"
                              class="w-full"
                              fluid
                              autocomplete="off"
                              [invalid]="(additionalFieldCtrl(field.fieldKey)?.invalid ?? false) && (additionalFieldCtrl(field.fieldKey)?.touched ?? false)"
                            />
                            <p-inputgroup-addon>{{ fieldTypeLabel(field.type) }}</p-inputgroup-addon>
                          </p-inputgroup>
                          <label [for]="'dyn_' + field.fieldKey">
                            {{ field.label | titlecase }}
                            @if (field.required) {
                              <span class="text-red-500">*</span>
                            }
                          </label>
                        </p-floatlabel>
                      }
                      @if (additionalFieldCtrl(field.fieldKey)?.hasError('required') && additionalFieldCtrl(field.fieldKey)?.touched) {
                        <small class="text-red-600">Este campo es obligatorio.</small>
                      } @else if (additionalFieldCtrl(field.fieldKey)?.hasError('pattern') && additionalFieldCtrl(field.fieldKey)?.touched) {
                        <small class="text-red-600">El valor no cumple el formato requerido.</small>
                      }
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Líneas de la venta -->
            <div class="mt-8 border-t border-slate-200 pt-6">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <i class="pi pi-list"></i>
                    Líneas de la venta
                  </h3>
                  <p class="text-sm text-slate-500">Agrega productos o servicios a la venta.</p>
                  @if (!canEditLines() && isEditMode()) {
                    <p class="text-sm text-slate-500 mt-1">{{ fieldReadonlyTooltip(SaleEditField.LINES) }}</p>
                  }
                </div>
                <p-button label="Agregar línea" icon="pi pi-plus" (onClick)="openLineModal('create')" type="button" [disabled]="!canEditLines()" [pTooltip]="fieldReadonlyTooltip(SaleEditField.LINES)" tooltipPosition="left" />
              </div>

              <div class="bg-white rounded-lg shadow-sm border border-slate-200 ">
                <div class="overflow-x-scroll">
                  <table class="w-full">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="w-10 px-2 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider"></th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Producto</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Cantidad</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">P. unitario</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Subtotal</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Impuestos</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Total</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-slate-200">
                      @if (lines().length === 0) {
                        <tr>
                          <td class="px-6 py-6 text-center text-sm text-slate-500" colspan="9">
                            @if (!canEditLines() && isEditMode()) {
                              Las líneas no se pueden modificar en el estado actual de la venta.
                            } @else {
                              No hay líneas. Haz clic en "Agregar línea" para comenzar.
                            }
                          </td>
                        </tr>
                      }
                      @for (line of lines(); track line.tempId || line.id || $index) {
                        <tr class="hover:bg-slate-50 transition-colors">
                          <td class="w-10 px-2 py-4 align-top">
                            @if (line.taxes?.length) {
                              <button
                                type="button"
                                class="p-1.5 rounded text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
                                (click)="toggleLineExpanded($index)"
                                [attr.aria-expanded]="isLineExpanded($index)"
                              >
                                <i class="pi text-sm" [class.pi-chevron-right]="!isLineExpanded($index)" [class.pi-chevron-down]="isLineExpanded($index)"></i>
                              </button>
                            }
                          </td>
                          <td class="px-0 py-4 text-sm text-slate-600 max-w-xs truncate">{{ line.description }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{{ line.quantity }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-right">{{ line.unitPrice | currency:'USD':'symbol':'1.2-2' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-right">{{ line.subtotal | currency:'USD':'symbol':'1.2-2' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-right">{{ line.taxAmount | currency:'USD':'symbol':'1.2-2' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 text-right">{{ line.totalAmount | currency:'USD':'symbol':'1.2-2' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-right">
                            <div class="flex justify-end gap-1">
                              <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" severity="secondary" (onClick)="openLineModal('edit', $index)" type="button" [disabled]="!canEditLines()" [pTooltip]="fieldReadonlyTooltip(SaleEditField.LINES)" tooltipPosition="left" />
                              <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="confirmRemoveLine($index)" type="button" [disabled]="!canEditLines() || lineRemoving()" [loading]="lineRemoving()" [pTooltip]="fieldReadonlyTooltip(SaleEditField.LINES)" tooltipPosition="left" />
                            </div>
                          </td>
                        </tr>
                        @if (isLineExpanded($index) && line.taxes?.length) {
                          <tr class="bg-slate-50/70">
                            <td colspan="9" class="px-6 py-3">
                              <div class="pl-8 text-sm">
                                <div class="font-medium text-slate-600 mb-2">Desglose de impuestos</div>
                                <table class="w-full max-w-md border border-slate-200 rounded-lg overflow-hidden">
                                  <thead>
                                    <tr class="bg-slate-100 border-b border-slate-200">
                                      <th class="px-3 py-2 text-left text-xs font-medium text-slate-600">Código</th>
                                      <th class="px-3 py-2 text-left text-xs font-medium text-slate-600">Tasa</th>
                                      <th class="px-3 py-2 text-left text-xs font-medium text-slate-600 min-w-[5rem]">Tipo</th>
                                      <th class="px-3 py-2 text-right text-xs font-medium text-slate-600">Base</th>
                                      <th class="px-3 py-2 text-right text-xs font-medium text-slate-600">Monto</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    @for (t of line.taxes; track t.taxCode || $index) {
                                      <tr class="border-b border-slate-100 last:border-0">
                                        <td class="px-3 py-2 text-slate-700">{{ t.taxCode || '-' }}</td>
                                        <td class="px-3 py-2 text-slate-700">{{ formatTaxRate(t.taxRate) }}</td>
                                        <td class="px-3 py-2 text-slate-700">{{ getTaxCollectionTypeLabel(t.collectionType) || '-' }}</td>
                                        <td class="px-3 py-2 text-slate-700 text-right">{{ t.taxBase != null ? (t.taxBase | currency:'USD':'symbol':'1.2-2') : '-' }}</td>
                                        <td class="px-3 py-2 text-slate-700 text-right">{{ t.taxAmount != null ? (t.taxAmount | currency:'USD':'symbol':'1.2-2') : '-' }}</td>
                                      </tr>
                                    }
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Totales -->
            <!-- Inputs ocultos para que los controles estén vinculados y la validez del form se actualice al agregar líneas -->
            <div class="hidden">
              <input type="number" formControlName="subtotal" />
              <input type="number" formControlName="tax" />
              <input type="number" formControlName="traslados" />
              <input type="number" formControlName="retenciones" />
              <input type="number" formControlName="total" />
            </div>

            <div class="mt-8 border-t border-slate-200 pt-6">
              <!-- Inputs ocultos para que los controles de totales mantengan el binding y la validez del form se actualice -->
              <div class="hidden">
                <input type="number" formControlName="subtotal" />
                <input type="number" formControlName="tax" />
                <input type="number" formControlName="traslados" />
                <input type="number" formControlName="retenciones" />
                <input type="number" formControlName="total" />
              </div>
              <div class="p-4 bg-slate-50 rounded-lg border border-slate-200 max-w-sm ml-auto">
                <h4 class="text-sm font-semibold text-slate-700 mb-3">Totales</h4>
                <div class="space-y-2 text-sm">
                  <div class="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>{{ totals().subtotal | number:'1.2-2' }} {{ currencyCode }}</span>
                  </div>
                  <div class="flex justify-between text-slate-600">
                    <span class="pl-2">Traslados</span>
                    <span>{{ totals().traslados | number:'1.2-2' }} {{ currencyCode }}</span>
                  </div>
                  <div class="flex justify-between text-slate-600">
                    <span class="pl-2">Retenciones</span>
                    <span>- {{ totals().retenciones | number:'1.2-2' }} {{ currencyCode }}</span>
                  </div>
                  <!--div class="flex justify-between text-slate-600">
                    <span>Impuestos</span>
                    <span>{{ totals().tax | number:'1.2-2' }} {{ currencyCode }}</span>
                  </div -->
                  <div class="flex justify-between font-semibold text-slate-900 pt-2 border-t border-slate-200">
                    <span>Total</span>
                    <span>{{ totals().total | number:'1.2-2' }} {{ currencyCode }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Controles ocultos para que el form actualice validez al cambiar totales -->
            <div class="hidden" aria-hidden="true">
              <input type="number" formControlName="subtotal" />
              <input type="number" formControlName="tax" />
              <input type="number" formControlName="traslados" />
              <input type="number" formControlName="retenciones" />
              <input type="number" formControlName="total" />
            </div>
            </div>
          </p-tabpanel>

          <p-tabpanel value="charges">
            <div class="py-4">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <i class="pi pi-credit-card"></i>
                  Recibos de pago
                </h3>
                <p-button
                  label="Actualizar"
                  icon="pi pi-refresh"
                  (onClick)="loadCharges()"
                  type="button"
                  [loading]="chargesLoading()"
                  [disabled]="chargesLoading()"
                  pTooltip="Actualiza la lista de pagos"
                  severity="secondary"
                  [rounded]="true"
                />
              </div>
              @if (chargesLoading()) {
                <div class="flex items-center gap-2 text-slate-600 py-8">
                  <i class="pi pi-spin pi-spinner"></i>
                  <span>Cargando cargos...</span>
                </div>
              } @else if (charges().length === 0) {
                <p class="text-slate-500 py-6">No hay cargos asociados a esta venta.</p>
              } @else {
                <p-menu #chargeActionsMenu [model]="chargeRowMenuItems()" [popup]="true" appendTo="body" />
                <div class="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
                  <table class="w-full min-w-[1120px]">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="w-10 px-2 py-3 text-left text-xs font-semibold text-slate-700 uppercase"></th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Status</th>
                        <th class="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Vigencia</th>
                        <th class="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">F. pago</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Referencia</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Método cobro</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Forma de pago</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Banco</th>
                        <th class="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Total</th>
                        <th class="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-200">
                      @for (c of charges(); track c.id ?? $index) {
                        <tr class="hover:bg-slate-50">
                          <td class="w-10 px-2 py-3 align-middle">
                            @if (c.id != null) {
                              <button
                                type="button"
                                class="p-1.5 rounded text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
                                (click)="toggleChargeExpanded(c)"
                                [attr.aria-expanded]="isChargeExpanded(c.id)"
                                pTooltip="Ver intentos de pago"
                                tooltipPosition="top"
                              >
                                @if (isChargeAttemptsLoading(c.id)) {
                                  <i class="pi pi-spin pi-spinner text-sm"></i>
                                } @else {
                                  <i
                                    class="pi text-sm"
                                    [class.pi-chevron-right]="!isChargeExpanded(c.id)"
                                    [class.pi-chevron-down]="isChargeExpanded(c.id)"
                                  ></i>
                                }
                              </button>
                            }
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                            <span class="inline-flex items-center gap-3">
                              @switch (c.status) {
                                @case ('PAID') {
                                  <span class="shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                    <i class="pi pi-check-circle"></i>
                                  </span>
                                  <span class="flex flex-col gap-0.5">
                                    <span>Pagado</span>
                                    @if (c.paymentNumber != null && c.numberOfPayments != null) {
                                      <span class="text-slate-500 text-xs">Pago {{ c.paymentNumber }}/{{ c.numberOfPayments }}</span>
                                    }
                                  </span>
                                }
                                @case ('PENDING') {
                                  <span class="shrink-0 h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                                    <i class="pi pi-clock"></i>
                                  </span>
                                  <span class="flex flex-col gap-0.5">
                                    <span>Pendiente</span>
                                    @if (c.paymentNumber != null && c.numberOfPayments != null) {
                                      <span class="text-slate-500 text-xs">Pago {{ c.paymentNumber }}/{{ c.numberOfPayments }}</span>
                                    }
                                  </span>
                                }
                                @case ('SCHEDULED') {
                                  <span class="shrink-0 h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                                    <i class="pi pi-calendar"></i>
                                  </span>
                                  <span class="flex flex-col gap-0.5">
                                    <span>Programado</span>
                                    @if (c.paymentNumber != null && c.numberOfPayments != null) {
                                      <span class="text-slate-500 text-xs">Pago {{ c.paymentNumber }}/{{ c.numberOfPayments }}</span>
                                    }
                                  </span>
                                }
                                @case ('FAILED') {
                                  <span class="shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                                    <i class="pi pi-times-circle"></i>
                                  </span>
                                  <span class="flex flex-col gap-0.5">
                                    <span>Fallido</span>
                                    @if (c.paymentNumber != null && c.numberOfPayments != null) {
                                      <span class="text-slate-500 text-xs">Pago {{ c.paymentNumber }}/{{ c.numberOfPayments }}</span>
                                    }
                                  </span>
                                }
                                @case ('EXPIRED') {
                                  <span class="shrink-0 h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                                    <i class="pi pi-ban"></i>
                                  </span>
                                  <span class="flex flex-col gap-0.5">
                                    <span>Expirado</span>
                                    @if (c.paymentNumber != null && c.numberOfPayments != null) {
                                      <span class="text-slate-500 text-xs">Pago {{ c.paymentNumber }}/{{ c.numberOfPayments }}</span>
                                    }
                                  </span>
                                }
                                @case ('CANCELLED') {
                                  <span class="shrink-0 h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                                    <i class="pi pi-minus-circle"></i>
                                  </span>
                                  <span class="flex flex-col gap-0.5">
                                    <span>Cancelado</span>
                                    @if (c.paymentNumber != null && c.numberOfPayments != null) {
                                      <span class="text-slate-500 text-xs">Pago {{ c.paymentNumber }}/{{ c.numberOfPayments }}</span>
                                    }
                                    @if (c.payment?.cancelReason) {
                                      <span class="text-slate-500 text-xs max-w-[16rem] truncate" [pTooltip]="c.payment?.cancelReason || ''">{{ c.payment?.cancelReason }}</span>
                                    }
                                  </span>
                                }
                                @default {
                                  <span class="shrink-0 h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                                    <i class="pi pi-question-circle"></i>
                                  </span>
                                  <span class="flex flex-col gap-0.5">
                                    <span>{{ c.status ?? 'Sin estado' }}</span>
                                    @if (c.paymentNumber != null && c.numberOfPayments != null) {
                                      <span class="text-slate-500 text-xs">Pago {{ c.paymentNumber }}/{{ c.numberOfPayments }}</span>
                                    }
                                  </span>
                                }
                              }
                            </span>
                          </td>
                          <td class="w-40 px-4 py-3 whitespace-nowrap">
                            @if (chargeCollectionValidity(c); as validity) {
                              <ng-container *ngTemplateOutlet="collectionValidityBar; context: { $implicit: validity }" />
                            } @else {
                              <span class="block text-center text-sm text-slate-700">-</span>
                            }
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-700 text-center whitespace-nowrap">{{ c.paidAt ? (c.paidAt | date:'dd/MM/yyyy') : '-' }}</td>
                          <td class="px-4 py-3 text-sm text-slate-700 whitespace-nowrap font-mono">{{ c.payment?.providerId || '-' }}</td>
                          <td class="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                            <span class="flex items-center justify-center gap-2">
                              <span class="">{{ getChargeCollectionMethodLabel(c.collectionMethod) }}</span>
                              @if (c.collectionMethod === CollectionMethodEnum.LINK && c.paymentLink?.link) {
                                <button
                                  type="button"
                                  class="flex items-center justify-center rounded-md text-primary-600 hover:bg-primary-50 transition-colors shrink-0"
                                  (click)="openChargeReceipt(c)"
                                  pTooltip="Ver recibo público"
                                  aria-label="Ver recibo público"
                                >
                                  <i class="pi pi-external-link"></i>
                                </button>
                              }
                            </span>
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                            {{ getPaymentFormLabel(c.payment?.paymentForm) }}
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                            {{ c.payment?.bank || '-' }}
                          </td>
                          <td class="px-4 py-3 text-sm font-medium text-slate-900 text-right whitespace-nowrap">{{ c.total != null ? (c.total | currency:'USD':'symbol':'1.2-2') : '-' }}</td>
                          <td class="px-4 py-3 text-right whitespace-nowrap">
                            <p-button
                              icon="pi pi-ellipsis-v"
                              [rounded]="true"
                              [text]="true"
                              severity="secondary"
                              type="button"
                              aria-label="Acciones del recibo"
                              pTooltip="Acciones"
                              [loading]="chargeCaptureLoadingId() === c.id"
                              [disabled]="chargeCaptureLoadingId() === c.id"
                              (onClick)="openChargeActionsMenu($event, c)"
                            />
                          </td>
                        </tr>
                        @if (isChargeExpanded(c.id)) {
                          <tr class="bg-slate-50/70">
                            <td colspan="10" class="px-4 py-3">
                              <div class="pl-8 text-sm">
                                <div class="font-medium text-slate-600 mb-2">Intentos de pago</div>
                                @if (isChargeAttemptsLoading(c.id)) {
                                  <div class="flex items-center gap-2 text-slate-500 py-2">
                                    <i class="pi pi-spin pi-spinner text-sm"></i>
                                    <span>Cargando intentos...</span>
                                  </div>
                                } @else if (getChargeAttempts(c.id).length === 0) {
                                  <p class="text-slate-500 py-1">No hay intentos de pago registrados.</p>
                                } @else {
                                  <table class="w-full max-w-5xl border border-slate-200 rounded-lg overflow-hidden">
                                    <thead>
                                      <tr class="bg-slate-100 border-b border-slate-200">
                                        <th class="px-3 py-2 text-center text-xs font-medium text-slate-600 whitespace-nowrap">F. de Pago</th>
                                        <th class="px-3 py-2 text-left text-xs font-medium text-slate-600 whitespace-nowrap">Forma de pago</th>
                                        <th class="px-3 py-2 text-left text-xs font-medium text-slate-600 whitespace-nowrap">Status</th>
                                        <th class="px-3 py-2 text-right text-xs font-medium text-slate-600 whitespace-nowrap">Monto</th>
                                        <th class="px-3 py-2 text-left text-xs font-medium text-slate-600 whitespace-nowrap">Referencia</th>
                                        <th class="px-3 py-2 text-left text-xs font-medium text-slate-600">Mensaje</th>
                                        <th class="px-3 py-2 text-left text-xs font-medium text-slate-600">Motivo de cancelación</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      @for (a of getChargeAttempts(c.id); track a.id ?? $index) {
                                        <tr class="border-b border-slate-100 last:border-0 bg-white">
                                          <td class="px-3 py-2 text-slate-700 text-center whitespace-nowrap">
                                            {{ a.attemptedAt ? (a.attemptedAt | date:'dd/MM/yyyy HH:mm') : '-' }}
                                          </td>
                                          <td class="px-3 py-2 text-slate-700 whitespace-nowrap">
                                            {{ getPaymentFormLabel(a.paymentForm) }}
                                          </td>
                                          <td class="px-3 py-2 text-slate-700 whitespace-nowrap">
                                            <span
                                              class="inline-flex items-center gap-1.5"
                                              [class.text-green-700]="a.status === 'PAID'"
                                              [class.text-red-700]="a.status === 'FAILED'"
                                              [class.text-amber-700]="a.status === 'PENDING'"
                                              [class.text-slate-500]="a.status === 'CANCELLED' || a.status === 'EXPIRED'"
                                            >
                                              {{ getPaymentAttemptStatusLabel(a.status) }}
                                            </span>
                                          </td>
                                          <td class="px-3 py-2 text-slate-700 text-right whitespace-nowrap">
                                            {{ a.amount != null ? (a.amount | number:'1.2-2') : '-' }}
                                          </td>
                                          <td class="px-3 py-2 text-slate-700 whitespace-nowrap font-mono">
                                            {{ a.providerId || '-' }}
                                          </td>
                                          <td class="px-3 py-2 text-slate-700 break-words max-w-md">
                                            {{ a.resultMessage || '-' }}
                                          </td>
                                          <td class="px-3 py-2 text-slate-700 break-words max-w-xs">
                                            {{ a.status === 'CANCELLED' ? (a.cancelReason || a.resultMessage || '-') : '-' }}
                                          </td>
                                        </tr>
                                      }
                                    </tbody>
                                  </table>
                                }
                              </div>
                            </td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                  <ng-template #collectionValidityBar let-validity>
                    <div class="flex w-40 min-w-0 flex-col gap-1">
                      <div class="flex items-center justify-between gap-1.5 text-[11px]" [class.text-slate-500]="!validity.expired" [class.text-red-600]="validity.expired">
                        <span pTooltip="F. cobro" tooltipPosition="top">{{ validity.start | date:'dd/MM/yyyy' }}</span>
                        <span class="inline-flex items-center gap-1" [class.font-medium]="validity.expired">
                          <span pTooltip="F. vencimiento" tooltipPosition="top">{{ validity.due | date:'dd/MM/yyyy' }}</span>
                          @if (validity.expired) {
                            <i
                              class="pi pi-exclamation-circle text-xs"
                              [pTooltip]="overdueTooltip(validity.due)"
                              tooltipPosition="top"
                            ></i>
                          }
                        </span>
                      </div>
                      <div
                        class="h-1.5 w-full overflow-hidden rounded-full bg-slate-200"
                        role="progressbar"
                        [attr.aria-valuenow]="validity.percent"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        [pTooltip]="validity.remainingLabel"
                        tooltipPosition="top"
                      >
                        <div class="h-full rounded-full transition-all" [class]="validity.barClass" [style.width.%]="validity.percent"></div>
                      </div>
                    </div>
                  </ng-template>
                </div>
              }
            </div>
          </p-tabpanel>

          <p-tabpanel value="cfdis">
            <div class="py-4">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <i class="pi pi-file"></i>
                  CFDI's
                </h3>
                <p-button
                  label="Actualizar"
                  icon="pi pi-refresh"
                  (onClick)="loadInvoices()"
                  type="button"
                  [loading]="invoicesLoading()"
                  [disabled]="invoicesLoading()"
                  pTooltip="Actualiza la lista de CFDIs"
                  severity="secondary"
                  [rounded]="true"
                />
              </div>
              @if (invoicesLoading()) {
                <div class="flex items-center gap-2 text-slate-600 py-8">
                  <i class="pi pi-spin pi-spinner"></i>
                  <span>Cargando CFDIs...</span>
                </div>
              } @else if (invoices().length === 0) {
                <p class="text-slate-500 py-6">No hay CFDIs asociados a esta venta.</p>
              } @else {
                <p-menu #invoiceActionsMenu [model]="invoiceRowMenuItems()" [popup]="true" appendTo="body" />
                <div class="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
                  <table class="w-full min-w-[880px]">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Folio</th>
                        <th class="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Fecha de emisión</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Status</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap hidden sm:table-cell">Motivo de cancelación</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap hidden sm:table-cell">CFDI relacionado</th>
                        <th class="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-200">
                      @for (inv of invoices(); track inv.id ?? $index) {
                        <tr class="hover:bg-slate-50">
                          <td class="px-4 py-3 text-sm text-slate-900">
                            <span class="inline-flex items-center gap-3">
                              <span class="shrink-0 h-10 w-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600">
                                <i class="pi pi-file"></i>
                              </span>
                              <span class="min-w-0 flex flex-col gap-0.5">
                                <span class="font-medium font-mono">{{ getInvoiceSerieFolio(inv) }}</span>
                                <span class="text-xs text-slate-500 font-mono truncate" [pTooltip]="inv.invoiceId || ''" tooltipPosition="top">
                                  {{ inv.invoiceId || '-' }}
                                </span>
                              </span>
                            </span>
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-700 text-center whitespace-nowrap">
                            {{ inv.issueDate ? (inv.issueDate | date:'dd/MM/yyyy HH:mm') : '-' }}
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                            {{ getCfdiStatusLabel(inv.status) }}
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-700 hidden sm:table-cell">
                            {{ getCancelMotivoLabel(inv) }}
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-700 hidden sm:table-cell">
                            @if (inv.relatedInvoice; as related) {
                              <span class="min-w-0 flex flex-col gap-0.5">
                                <span class="font-medium font-mono">{{ getInvoiceSerieFolio(related) }}</span>
                                <span class="text-xs text-slate-500 font-mono truncate" [pTooltip]="related.invoiceId || ''" tooltipPosition="top">
                                  {{ related.invoiceId || '-' }}
                                </span>
                              </span>
                            } @else {
                              <span class="text-slate-400">-</span>
                            }
                          </td>
                          <td class="px-4 py-3 text-right whitespace-nowrap">
                            <p-button
                              icon="pi pi-ellipsis-v"
                              [rounded]="true"
                              [text]="true"
                              severity="secondary"
                              type="button"
                              aria-label="Acciones del CFDI"
                              pTooltip="Acciones"
                              [loading]="cfdiPdfViewerOpening() || downloadPdfLoading() || downloadXmlLoading() || resendCfdiLoading() || cancelCfdiLoading()"
                              [disabled]="cfdiPdfViewerOpening() || downloadPdfLoading() || downloadXmlLoading() || resendCfdiLoading() || cancelCfdiLoading()"
                              (onClick)="openInvoiceActionsMenu($event, inv)"
                            />
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
            </div>
          </p-tabpanel>

          <p-tabpanel value="attachments">
            <div class="py-4">
              @if (!editingSaleId()) {
                <p class="text-slate-500 py-6">Guarda la venta para habilitar adjuntos.</p>
              } @else {
                <div class="flex items-center justify-between mb-4">
                  <div>
                    <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <i class="pi pi-paperclip"></i>
                      Adjuntos
                    </h3>
                    <p class="text-sm text-slate-500">Sube documentos relacionados con la venta (máximo 10 MB por archivo).</p>
                  </div>
                  <div class="flex items-center gap-2">
                    <input #attachmentInput type="file" class="hidden" multiple (change)="onAttachmentSelect($event)" />
                    <p-button
                      type="button"
                      [label]="uploadingAttachments() ? 'Subiendo...' : 'Subir archivo'"
                      [icon]="uploadingAttachments() ? 'pi pi-spin pi-spinner' : 'pi pi-upload'"
                      [loading]="uploadingAttachments()"
                      [disabled]="uploadingAttachments()"
                      (onClick)="attachmentInput.click()"
                    />
                  </div>
                </div>
                @if (attachmentsLoading()) {
                  <div class="flex items-center gap-2 text-slate-600 py-8">
                    <i class="pi pi-spin pi-spinner"></i>
                    <span>Cargando adjuntos...</span>
                  </div>
                } @else if (attachments().length === 0) {
                  <p class="text-slate-500 py-6">No hay archivos adjuntos.</p>
                } @else {
                  <div class="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
                    <table class="w-full min-w-[640px]">
                      <thead class="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Archivo</th>
                          <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap hidden sm:table-cell">Tipo</th>
                          <th class="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Tamaño</th>
                          <th class="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase whitespace-nowrap hidden sm:table-cell">Fecha</th>
                          <th class="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Acciones</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-slate-200">
                        @for (file of attachments(); track file.id) {
                          <tr class="hover:bg-slate-50">
                            <td class="px-4 py-3">
                              <span class="flex items-center gap-2 text-sm font-medium text-slate-900">
                                <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600">
                                  <i [class]="'pi ' + getFileIcon(file.contentType)"></i>
                                </span>
                                <span class="truncate max-w-xs">{{ file.name }}</span>
                              </span>
                            </td>
                            <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600 hidden sm:table-cell">{{ file.contentType || '-' }}</td>
                            <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600 text-right">{{ formatFileSize(file.size) }}</td>
                            <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600 text-center hidden sm:table-cell">
                              {{ file.createdAt ? (file.createdAt | date:'dd/MM/yyyy HH:mm') : '-' }}
                            </td>
                            <td class="px-4 py-3 whitespace-nowrap text-right">
                              <p-button icon="pi pi-download" [rounded]="true" [text]="true" severity="secondary" styleClass="hover:!bg-sky-100" (onClick)="openAttachment(file)" />
                              <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="confirmDeleteAttachment(file)" />
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                }
              }
            </div>
          </p-tabpanel>

          <p-tabpanel value="auditoria">
            @if (activeTab() === 'auditoria') {
              <app-entity-audit-panel
                entityType="Sale"
                [entityId]="editingSaleId()"
                entityLabel="venta"
              />
            }
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <ng-template pTemplate="footer">
        <div class="!mt-4 flex justify-end gap-4 mt-1 !p-4 !-m-5 bg-gray-50 p-2 border-t border-slate-200">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancel()" type="button" />
          @if (showInvoicingProfileUpdateInFooter()) {
            <p-button
              label="Actualizar perfil de facturación"
              icon="pi pi-save"
              type="button"
              (onClick)="submitInvoicingProfileUpdate()"
              [loading]="invoicingProfileUpdateLoading()"
              [disabled]="loading() || invoicingProfileUpdateLoading() || !invoicingProfileSubsetDirty()"
            />
          }
          @if (showSaleUpdateInFooter()) {
            <p-button
              label="Guardar cambios"
              icon="pi pi-save"
              type="button"
              (onClick)="submitAllowedSaleUpdate()"
              [loading]="loading()"
              [disabled]="loading() || !footerDraftSubmitEnabled()"
              appDisableUnless="SALE_WRITE"
            />
          }
          @if (!saleIsClosedOperationalFooter()) {
            <div class="flex flex-wrap items-center justify-end gap-3">
              <p-button
                [label]="isEditMode() ? 'Guardar borrador' : 'Crear borrador'"
                icon="pi pi-save"
                severity="secondary"
                [outlined]="true"
                (onClick)="onSubmitWithStatus('DRAFT')"
                [loading]="loading()"
                [disabled]="loading() || !footerDraftSubmitEnabled()"
                type="button"
              />
              <p-button
                label="Cerrar venta"
                icon="pi pi-check-circle"
                (onClick)="confirmCloseSale()"
                [disabled]="loading() || !canEditLines() || !footerDraftSubmitEnabled()"
                [loading]="loading()"
                type="button"
                appDisableUnless="SALE_WRITE"
              />
            </div>
          }
        </div>
      </ng-template>
    </p-card>
  </form>
  </div>
</div>

@if (seguimientoDialogChargeId(); as chargeId) {
  <app-charge-followup-dialog
    [visible]="seguimientoDialogVisible()"
    (visibleChange)="onSeguimientoDialogVisibleChange($event)"
    [chargeId]="chargeId"
    [saleId]="editingSaleId()"
    [subtitle]="seguimientoDialogSubtitle()"
  />
}

<p-dialog
  [visible]="cancelSaleConfirmVisible()"
  (visibleChange)="!$event ? cancelCancelSaleConfirm() : cancelSaleConfirmVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!cancelSaleLoading()"
  [closable]="!cancelSaleLoading()"
  appendTo="body"
  [style]="{ width: 'min(28rem, calc(100vw - 2rem))' }"
  styleClass="sale-close-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-100 to-orange-50 text-rose-700 shadow-inner ring-1 ring-rose-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-ban text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Cancelar venta</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Anula la venta y sus cobros pendientes</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      Vas a <span class="font-medium text-slate-800">cancelar esta venta</span>. Pasará a estado cancelado y se
      anularán los cargos pendientes junto con sus ligas de pago.
    </p>
    <div class="mt-4">
      <p-floatlabel variant="on">
        <textarea
          pTextarea
          id="cancel-sale-reason"
          rows="3"
          [maxlength]="cancelReasonMaxLength"
          fluid
          [ngModel]="cancelSaleReason()"
          (ngModelChange)="cancelSaleReason.set($event ?? '')"
          [disabled]="cancelSaleLoading()"
        ></textarea>
        <label for="cancel-sale-reason">Motivo de cancelación (opcional)</label>
      </p-floatlabel>
      <p class="mt-1 text-right text-xs text-slate-400">{{ cancelSaleReason().length }}/{{ cancelReasonMaxLength }}</p>
    </div>
    @if (cancelSaleError()) {
      <div class="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
        <i class="pi pi-exclamation-triangle text-red-600 mt-0.5 shrink-0" aria-hidden="true"></i>
        <p class="text-sm text-red-800">{{ cancelSaleError() }}</p>
      </div>
    }
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Volver"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="cancelSaleLoading()"
        (onClick)="cancelCancelSaleConfirm()"
      />
      <p-button
        label="Sí, cancelar venta"
        icon="pi pi-ban"
        severity="danger"
        type="button"
        [loading]="cancelSaleLoading() && !cancelPaymentsConfirmVisible()"
        [disabled]="cancelSaleLoading() || cancelSaleReason().length > cancelReasonMaxLength"
        (onClick)="continueCancelSale()"
        appDisableUnless="SALE_WRITE"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="cancelPaymentsConfirmVisible()"
  (visibleChange)="onCancelPaymentsDialogVisibleChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!cancelSaleLoading()"
  [closable]="!cancelSaleLoading()"
  appendTo="body"
  [style]="{ width: 'min(28rem, calc(100vw - 2rem))' }"
  styleClass="sale-close-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-50 text-amber-700 shadow-inner ring-1 ring-amber-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-exclamation-triangle text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Cancelar pagos aplicados</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">La venta tiene cargos con pago aplicado</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      Esta venta cuenta con
      <span class="font-medium text-slate-800">
        @if (appliedPaymentsCount() > 0) {
          {{ appliedPaymentsCount() }} {{ appliedPaymentsCount() === 1 ? 'pago aplicado' : 'pagos aplicados' }}
        } @else {
          pagos aplicados
        }
      </span>.
      La cancelación de la venta <span class="font-medium text-slate-800">cancelará los pagos aplicados</span>
      y los cargos asociados.
    </p>
    @if (cancelSaleError()) {
      <div class="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
        <i class="pi pi-exclamation-triangle text-red-600 mt-0.5 shrink-0" aria-hidden="true"></i>
        <p class="text-sm text-red-800">{{ cancelSaleError() }}</p>
      </div>
    }
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Volver"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="cancelSaleLoading()"
        (onClick)="backToCancelSaleConfirm()"
      />
      <p-button
        label="Sí, cancelar venta y pagos"
        icon="pi pi-ban"
        severity="danger"
        type="button"
        [loading]="cancelSaleLoading()"
        [disabled]="cancelSaleLoading()"
        (onClick)="executeCancelSale()"
        appDisableUnless="SALE_WRITE"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="cancelPaymentDialogVisible()"
  (visibleChange)="!$event ? closeCancelPaymentDialog() : cancelPaymentDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!cancelPaymentLoading()"
  [closable]="!cancelPaymentLoading()"
  appendTo="body"
  [style]="{ width: 'min(26rem, calc(100vw - 2rem))' }"
  styleClass="sale-close-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-100 to-orange-50 text-rose-700 shadow-inner ring-1 ring-rose-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-ban text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Cancelar pago aplicado</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">El cargo volverá a quedar pendiente de cobro</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      Vas a <span class="font-medium text-slate-800">cancelar el pago aplicado</span> de este cargo.
      El recibo quedará pendiente y se actualizará el saldo de la venta.
    </p>
    <div class="mt-4">
      <p-floatlabel variant="on">
        <textarea
          pTextarea
          id="cancel-payment-reason"
          rows="3"
          [maxlength]="cancelReasonMaxLength"
          fluid
          [ngModel]="cancelPaymentReason()"
          (ngModelChange)="cancelPaymentReason.set($event ?? '')"
          [disabled]="cancelPaymentLoading()"
        ></textarea>
        <label for="cancel-payment-reason">Motivo de cancelación (opcional)</label>
      </p-floatlabel>
      <p class="mt-1 text-right text-xs text-slate-400">{{ cancelPaymentReason().length }}/{{ cancelReasonMaxLength }}</p>
    </div>
    @if (cancelPaymentError()) {
      <div class="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
        <i class="pi pi-exclamation-triangle text-red-600 mt-0.5 shrink-0" aria-hidden="true"></i>
        <p class="text-sm text-red-800">{{ cancelPaymentError() }}</p>
      </div>
    }
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Volver"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="cancelPaymentLoading()"
        (onClick)="closeCancelPaymentDialog()"
      />
      <p-button
        label="Sí, cancelar pago"
        icon="pi pi-ban"
        severity="danger"
        type="button"
        [loading]="cancelPaymentLoading()"
        [disabled]="cancelPaymentLoading() || cancelPaymentReason().length > cancelReasonMaxLength"
        (onClick)="executeCancelPayment()"
        appDisableUnless="CHARGE_WRITE"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="deleteSaleConfirmVisible()"
  (visibleChange)="!$event ? cancelDeleteSaleConfirm() : deleteSaleConfirmVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!deleteSaleLoading()"
  [closable]="!deleteSaleLoading()"
  appendTo="body"
  [style]="{ width: 'min(26rem, calc(100vw - 2rem))' }"
  styleClass="sale-close-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-100 to-orange-50 text-rose-700 shadow-inner ring-1 ring-rose-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-trash text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Eliminar venta</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Esta acción no se puede deshacer</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      ¿Está seguro de eliminar la venta con folio
      <span class="font-medium text-slate-800">"{{ folioDisplayValue() }}"</span>?
      Esta acción no se puede deshacer.
    </p>
    @if (deleteSaleError()) {
      <div class="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
        <i class="pi pi-exclamation-triangle text-red-600 mt-0.5 shrink-0" aria-hidden="true"></i>
        <p class="text-sm text-red-800">{{ deleteSaleError() }}</p>
      </div>
    }
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Volver"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="deleteSaleLoading()"
        (onClick)="cancelDeleteSaleConfirm()"
      />
      <p-button
        label="Sí, eliminar"
        icon="pi pi-trash"
        severity="danger"
        type="button"
        [loading]="deleteSaleLoading()"
        [disabled]="deleteSaleLoading()"
        (onClick)="executeDeleteSale()"
        appDisableUnless="SALE_WRITE"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="closeSaleConfirmVisible()"
  (visibleChange)="closeSaleConfirmVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="true"
  [closable]="true"
  appendTo="body"
  [style]="{ width: 'min(26rem, calc(100vw - 2rem))' }"
  styleClass="sale-close-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-50 text-amber-700 shadow-inner ring-1 ring-amber-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-exclamation-triangle text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Cerrar venta</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Acción definitiva en el flujo de cobro</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      Este paso es <span class="font-medium text-slate-800">irreversible</span>. La venta pasará a estado cerrado y se
      habilitará el proceso de <span class="font-medium text-slate-800">cobro al cliente</span> según los términos y el
      método de cobro configurados.
    </p>
    <ul class="mt-4 space-y-2.5 text-sm text-slate-700">
      <li class="flex gap-3">
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          aria-hidden="true"
        >
          <i class="pi pi-ban text-xs"></i>
        </span>
        <span>No podrás volver a editarla como borrador desde este formulario.</span>
      </li>
      <li class="flex gap-3">
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          aria-hidden="true"
        >
          <i class="pi pi-credit-card text-xs"></i>
        </span>
        <span>Se procederá a programar los pagos según los términos de pago configurados.</span>
      </li>
    </ul>
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Cancelar"
        severity="secondary"
        [outlined]="true"
        type="button"
        (onClick)="cancelCloseSaleConfirm()"
      />
      <p-button
        label="Sí, cerrar venta"
        icon="pi pi-check"
        severity="success"
        type="button"
        styleClass="!bg-emerald-600 !border-emerald-600 hover:!bg-emerald-700 hover:!border-emerald-700"
        (onClick)="confirmCloseSaleSubmit()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="fullEditSaveConfirmVisible()"
  (visibleChange)="onFullEditSaveConfirmVisibleChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="true"
  [closable]="true"
  appendTo="body"
  [style]="{ width: 'min(26rem, calc(100vw - 2rem))' }"
  styleClass="sale-full-edit-save-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-50 text-amber-700 shadow-inner ring-1 ring-amber-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-exclamation-triangle text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Regenerar cargos</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Cambio financiero detectado</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      {{ fullEditSaveConfirmMessage }}
    </p>
  </div>

  <ng-template #footer>
    <div
      class="flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Cancelar"
        severity="secondary"
        [outlined]="true"
        type="button"
        (onClick)="cancelFullEditSaveConfirm()"
      />
      <p-button
        label="Sí, guardar"
        icon="pi pi-check"
        type="button"
        (onClick)="confirmFullEditSaveSubmit()"
        appDisableUnless="SALE_WRITE"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="stampSaleConfirmVisible()"
  (visibleChange)="onStampSaleConfirmVisibleChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="true"
  [closable]="true"
  appendTo="body"
  [style]="{ width: 'min(34rem, calc(100vw - 2rem))' }"
  styleClass="sale-stamp-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-slate-50 text-sky-700 shadow-inner ring-1 ring-sky-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-file-export text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Facturar (timbrar CFDI)</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Emisión fiscal ante el SAT</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      Vas a <span class="font-medium text-slate-800">generar y timbrar el comprobante fiscal (CFDI)</span> asociado a esta
      venta. Revisa los datos fiscales que se usarán; una vez timbrado, el XML y el PDF quedarán disponibles para descarga.
    </p>

    @if (stampFiscalSummary(); as fiscal) {
      <div class="mt-4 max-h-[min(52vh,28rem)] overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-slate-50">
        <div class="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-white sticky top-0 z-10">
          <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100 text-sky-600" aria-hidden="true">
            <i class="pi pi-id-card text-sm"></i>
          </span>
          <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Datos fiscales</span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 p-4">
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Razón social</p>
            <p class="text-sm font-medium text-slate-900 break-words">{{ fiscal.legalName }}</p>
            @if (fiscal.comercialName !== '—' && fiscal.comercialName !== fiscal.legalName) {
              <p class="text-xs text-slate-500 mt-0.5">{{ fiscal.comercialName }}</p>
            }
          </div>
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">RFC</p>
            <p class="text-sm text-slate-800 font-mono tracking-wide">{{ fiscal.taxId }}</p>
          </div>
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Dirección fiscal</p>
            <p class="text-sm text-slate-800 break-words">{{ fiscal.address }}</p>
          </div>
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Régimen fiscal</p>
            <p class="text-sm text-slate-800 break-words">{{ fiscal.taxRegime }}</p>
          </div>
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Uso de CFDI</p>
            <p class="text-sm text-slate-800 break-words">{{ fiscal.cfdiUse }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Método de pago</p>
            <p class="text-sm text-slate-800 break-words">{{ fiscal.paymentMethod }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Forma de pago</p>
            <p class="text-sm text-slate-800 break-words">{{ fiscal.paymentForm }}</p>
          </div>
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Correo</p>
            <p class="text-sm text-slate-800 break-all">{{ fiscal.email }}</p>
          </div>
        </div>

        <div class="border-t border-slate-200 px-4 py-3 space-y-2">
          @for (line of lines(); track line.tempId || line.id || $index) {
            <div class="flex items-start justify-between gap-3 text-sm">
              <p class="min-w-0 flex-1 text-slate-700 break-words">
                {{ line.description }}
                <span class="text-slate-400 tabular-nums"> · {{ line.quantity | number:'1.0-4' }}</span>
              </p>
              <p class="shrink-0 tabular-nums text-slate-800">
                {{ line.totalAmount | currency:currencyCode:'symbol':'1.2-2' }}
              </p>
            </div>
          }
          <div class="flex justify-between gap-3 pt-2 text-sm text-slate-600 border-t border-slate-200">
            <span>Impuestos</span>
            <span class="tabular-nums">{{ totals().tax | currency:currencyCode:'symbol':'1.2-2' }}</span>
          </div>
          <div class="flex justify-between gap-3 text-sm font-semibold text-slate-900">
            <span>Total</span>
            <span class="tabular-nums">{{ totals().total | currency:currencyCode:'symbol':'1.2-2' }}</span>
          </div>
        </div>
      </div>
    }

    <p class="mt-4 flex gap-2 text-sm text-slate-600">
      <span
        class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600"
        aria-hidden="true"
      >
        <i class="pi pi-lock text-xs"></i>
      </span>
      <span>El timbrado tiene efectos fiscales; una vez emitida, no se puede modificar.</span>
    </p>
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Cancelar"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="stampSaleLoading()"
        (onClick)="cancelStampSaleConfirm()"
      />
      <p-button
        label="Sí, facturar"
        icon="pi pi-check"
        severity="success"
        type="button"
        styleClass="!bg-emerald-600 !border-emerald-600 hover:!bg-emerald-700 hover:!border-emerald-700"
        [loading]="stampSaleLoading()"
        [disabled]="stampSaleLoading()"
        (onClick)="proceedStampSaleAfterSummary()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="stampPaymentForm99WarningVisible()"
  (visibleChange)="onStampPaymentForm99WarningVisibleChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!stampSaleLoading()"
  [closable]="!stampSaleLoading()"
  appendTo="body"
  [style]="{ width: 'min(30rem, calc(100vw - 2rem))' }"
  styleClass="sale-stamp-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-50 text-amber-700 shadow-inner ring-1 ring-amber-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-exclamation-triangle text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Forma de pago por definir</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Confirmación adicional antes de timbrar</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      La forma de pago seleccionada es
      <span class="font-medium text-slate-800">«99 - Por definir»</span>.
      Al timbrar el CFDI con esta clave, el SAT interpretará que el pago aún no se ha definido.
    </p>
    <div class="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
      <i class="pi pi-info-circle text-amber-600 mt-0.5 shrink-0" aria-hidden="true"></i>
      <p class="text-sm text-amber-900">
        ¿Deseas continuar con el timbrado usando forma de pago «99 - Por definir»?
      </p>
    </div>
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Cancelar"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="stampSaleLoading()"
        (onClick)="cancelStampPaymentForm99Warning()"
      />
      <p-button
        label="Sí, continuar"
        icon="pi pi-check"
        severity="warn"
        type="button"
        [loading]="stampSaleLoading()"
        [disabled]="stampSaleLoading()"
        (onClick)="confirmStampPaymentForm99Warning()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="cfdiPdfViewerVisible()"
  (visibleChange)="onCfdiPdfViewerVisibleChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="true"
  [dismissableMask]="true"
  [closable]="true"
  appendTo="body"
  [style]="{ width: 'min(56rem, calc(100vw - 1.5rem))' }"
  [contentStyle]="{ padding: '0' }"
  styleClass="cfdi-pdf-viewer-dialog"
  header="CFDI — vista PDF"
  [focusOnShow]="true"
>
  @if (cfdiPdfViewerSafeUrl(); as pdfSrc) {
    <iframe
      [src]="pdfSrc"
      class="block min-h-[72vh] w-full rounded-b-lg border-0 bg-slate-100"
      title="Vista previa del PDF del CFDI"
    ></iframe>
  } @else {
    <div class="flex min-h-[12rem] items-center justify-center text-slate-500">
      <i class="pi pi-spin pi-spinner mr-2"></i>
      <span>Cargando vista previa…</span>
    </div>
  }
</p-dialog>

<p-dialog
  [visible]="recallEmailDialogVisible()"
  (visibleChange)="!$event ? closeRecallEmailDialog() : recallEmailDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!recallLoading()"
  [closable]="!recallLoading()"
  appendTo="body"
  [style]="{ width: 'min(28rem, calc(100vw - 2rem))' }"
  header="Enviar liga de pago"
>
  <div class="flex flex-col gap-4 pt-1">
    @if (recallConvertsManualCharge()) {
      <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
        <i class="pi pi-exclamation-triangle text-amber-600 mt-0.5 shrink-0"></i>
        <p class="text-sm text-amber-900 m-0">
          Esta es una venta con cobro <strong>manual</strong>. Al enviar, el recibo pasará a
          <strong>liga de pago</strong> y se generará el enlace para el cliente.
        </p>
      </div>
    }
    <p class="text-sm text-slate-600 m-0">
      Indica el correo al que se enviará el enlace de pago del recibo.
    </p>
    <p-floatlabel variant="on">
      <input
        pInputText
        id="recall-email"
        class="w-full"
        type="email"
        autocomplete="email"
        [ngModel]="recallEmail()"
        (ngModelChange)="recallEmail.set($event)"
        [disabled]="recallLoading()"
      />
      <label for="recall-email">Correo electrónico</label>
    </p-floatlabel>
  </div>
  <ng-template #footer>
    <div class="flex justify-end gap-2">
      <p-button
        label="Cancelar"
        severity="secondary"
        [text]="true"
        type="button"
        [disabled]="recallLoading()"
        (onClick)="closeRecallEmailDialog()"
      />
      <p-button
        label="Enviar"
        icon="pi pi-send"
        type="button"
        [loading]="recallLoading()"
        [disabled]="recallLoading() || !recallEmail().trim()"
        (onClick)="submitRecallEmail()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="resendCfdiDialogVisible()"
  (visibleChange)="!$event ? closeResendCfdiDialog() : resendCfdiDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!resendCfdiLoading()"
  [closable]="!resendCfdiLoading()"
  appendTo="body"
  [style]="{ width: 'min(26rem, calc(100vw - 2rem))' }"
  header="Reenviar CFDI"
>
  <div class="flex flex-col gap-4 pt-1">
    <p class="text-sm text-slate-600 m-0">
      Indica el correo al que se enviará el PDF y el XML del CFDI.
    </p>
    <p-floatlabel variant="on">
      <input
        pInputText
        id="resend-cfdi-email"
        class="w-full"
        type="email"
        autocomplete="email"
        [ngModel]="resendCfdiEmail()"
        (ngModelChange)="resendCfdiEmail.set($event)"
        [disabled]="resendCfdiLoading()"
      />
      <label for="resend-cfdi-email">Correo electrónico</label>
    </p-floatlabel>
  </div>
  <ng-template #footer>
    <div class="flex justify-end gap-2">
      <p-button
        label="Cancelar"
        severity="secondary"
        [text]="true"
        type="button"
        [disabled]="resendCfdiLoading()"
        (onClick)="closeResendCfdiDialog()"
      />
      <p-button
        label="Enviar"
        icon="pi pi-send"
        type="button"
        [loading]="resendCfdiLoading()"
        [disabled]="resendCfdiLoading() || !resendCfdiEmail().trim()"
        (onClick)="submitResendCfdi()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="cancelCfdiDialogVisible()"
  (visibleChange)="!$event ? closeCancelCfdiDialog() : cancelCfdiDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!cancelCfdiLoading()"
  [closable]="!cancelCfdiLoading()"
  appendTo="body"
  [style]="{ width: 'min(32rem, calc(100vw - 2rem))' }"
  contentStyleClass="!overflow-visible"
  header="Cancelar CFDI"
>
  <div class="flex flex-col gap-4 pt-1">
    <p class="text-sm text-slate-600 m-0">
      Esta acción solicita la cancelación del CFDI ante el SAT. Solo se pueden cancelar comprobantes vigentes.
    </p>
    <p-floatlabel variant="on" class="w-full">
      <p-select
        inputId="cancel-cfdi-motivo"
        class="w-full"
        [options]="cfdiCancelMotivoSelectOptions()"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        [overlayOptions]="{ appendTo: 'body' }"
        [panelStyle]="{ width: 'max-content', minWidth: '100%' }"
        [ngModel]="cancelCfdiMotivo()"
        (ngModelChange)="onCancelCfdiMotivoChange($event)"
        [disabled]="cancelCfdiLoading()"
      >
        <ng-template #selectedItem let-option>
          <span class="block truncate">{{ option.label }}</span>
        </ng-template>
        <ng-template #item let-option>
          <span class="whitespace-nowrap">{{ option.label }}</span>
        </ng-template>
      </p-select>
      <label for="cancel-cfdi-motivo">Motivo de cancelación</label>
    </p-floatlabel>
    @if (selectedCancelCfdiMotivo()?.requiresRelatedCfdi) {
      <p-floatlabel variant="on">
        <input
          pInputText
          id="cancel-cfdi-uuid"
          class="w-full"
          type="text"
          maxlength="36"
          autocomplete="off"
          [ngModel]="cancelCfdiFolioSustitucion()"
          (ngModelChange)="cancelCfdiFolioSustitucion.set($event)"
          [disabled]="cancelCfdiLoading()"
        />
        <label for="cancel-cfdi-uuid">UUID del CFDI que sustituye</label>
      </p-floatlabel>
    }
  </div>
  <ng-template #footer>
    <div class="flex justify-end gap-2">
      <p-button
        label="Cerrar"
        severity="secondary"
        [text]="true"
        type="button"
        [disabled]="cancelCfdiLoading()"
        (onClick)="closeCancelCfdiDialog()"
      />
      <p-button
        label="Cancelar CFDI"
        icon="pi pi-times-circle"
        severity="danger"
        type="button"
        [loading]="cancelCfdiLoading()"
        [disabled]="cancelCfdiLoading() || !cancelCfdiMotivo() || (!!selectedCancelCfdiMotivo()?.requiresRelatedCfdi && !cancelCfdiFolioSustitucion().trim())"
        (onClick)="submitCancelCfdi()"
        appDisableUnless="INVOICE_CANCEL"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="payChargeDialogVisible()"
  (visibleChange)="!$event ? closePayChargeDialog() : payChargeDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!payChargeLoading()"
  [closable]="!payChargeLoading()"
  appendTo="body"
  [style]="{ width: 'min(28rem, calc(100vw - 2rem))' }"
  styleClass="[&_.p-dialog-footer]:!p-0"
  [header]="payChargeMode() === 'edit' ? 'Editar pago' : 'Registrar pago'"
>
  <form [formGroup]="payChargeForm" class="flex flex-col gap-4 pt-1">
    <p class="text-sm text-slate-600 m-0">
      @if (payChargeMode() === 'edit') {
        Actualiza los datos del pago registrado para este recibo.
      } @else {
        Registra el pago recibido para este recibo. Se creará el registro de pago y el intento asociado.
      }
    </p>
    @if (payChargeMode() === 'create' && payChargeScheduledStart()) {
      <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
        <i class="pi pi-exclamation-triangle text-amber-600 mt-0.5 shrink-0"></i>
        <p class="text-sm text-amber-900 m-0">
          Este recibo está <strong>programado</strong> e inicia cobro el
          <strong>{{ payChargeScheduledStart() | date:'dd/MM/yyyy' }}</strong>. Al registrar el pago por adelantado
          se bloqueará la edición de montos de la venta@if (currentSale()?.autoInvoice) {<span> y la factura se timbrará hoy</span>}.
        </p>
      </div>
    }
    <p-floatlabel variant="on">
      <p-select
        formControlName="paymentFormId"
        inputId="pay-payment-form"
        [options]="paymentFormOptions()"
        optionLabel="description"
        optionValue="id"
        placeholder="Selecciona una forma de pago"
        filter
        [showClear]="true"
        class="w-full"
        [disabled]="payChargeLoading()"
      />
      <label for="pay-payment-form">Forma de pago <span class="text-red-500">*</span></label>
    </p-floatlabel>
    <p-floatlabel variant="on">
      <input
        pInputText
        id="pay-reference"
        class="w-full"
        formControlName="reference"
        maxlength="50"
        [disabled]="payChargeLoading()"
      />
      <label for="pay-reference">Referencia <span class="text-red-500">*</span></label>
    </p-floatlabel>
    <p-floatlabel variant="on">
      <input
        pInputText
        id="pay-bank"
        class="w-full"
        formControlName="bank"
        maxlength="255"
        [disabled]="payChargeLoading()"
      />
      <label for="pay-bank">Banco</label>
    </p-floatlabel>
    <p-floatlabel variant="on">
      <textarea
        pTextarea
        id="pay-description"
        formControlName="description"
        rows="3"
        [maxlength]="1000"
        fluid
        [disabled]="payChargeLoading()"
      ></textarea>
      <label for="pay-description">Descripción</label>
    </p-floatlabel>
    <p-floatlabel variant="on">
      <p-datepicker
        formControlName="paymentDate"
        inputId="pay-payment-date"
        [showTime]="true"
        hourFormat="24"
        dateFormat="dd/mm/yy"
        placeholder="dd/mm/aaaa hh:mm"
        fluid
        appendTo="body"
        [disabled]="payChargeLoading()"
      />
      <label for="pay-payment-date">Fecha y hora de pago <span class="text-red-500">*</span></label>
    </p-floatlabel>
    <div class="flex items-center gap-2">
      <p-checkbox
        formControlName="sendReceiptEmail"
        inputId="pay-send-receipt-email"
        [binary]="true"
        [disabled]="payChargeLoading()"
      />
      <label for="pay-send-receipt-email" class="cursor-pointer select-none text-sm text-slate-700">
        Enviar correo de recibo de pago al cliente
      </label>
    </div>
  </form>
  <ng-template #footer>
    <div class="flex w-full items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 pt-4 pb-4">
      <p-button
        label="Cancelar"
        severity="secondary"
        [text]="true"
        type="button"
        [disabled]="payChargeLoading()"
        (onClick)="closePayChargeDialog()"
      />
      <p-button
        [label]="payChargeMode() === 'edit' ? 'Guardar' : 'Pagar'"
        [icon]="payChargeMode() === 'edit' ? 'pi pi-save' : 'pi pi-check'"
        type="button"
        [loading]="payChargeLoading()"
        appDisableUnless="CHARGE_WRITE"
        [disabled]="payChargeLoading() || payChargeForm.invalid"
        (onClick)="submitPayCharge()"
      />
    </div>
  </ng-template>
</p-dialog>

<!-- Modal Agregar/Editar línea -->
@if (lineModalOpen()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div
      class="absolute inset-0 bg-black transition-opacity duration-200"
      [class.bg-opacity-40]="lineModalShown()"
      [class.bg-opacity-0]="!lineModalShown()"
      (click)="closeLineModal()"
    ></div>
    <div
      class="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl transform transition-all duration-200"
      [class.opacity-100]="lineModalShown()"
      [class.opacity-0]="!lineModalShown()"
      [class.scale-100]="lineModalShown()"
      [class.scale-95]="!lineModalShown()"
    >
      <div
        class="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-slate-100 px-4 py-3"
      >
        <h3 class="text-lg font-semibold text-slate-900">
          {{ lineModalMode() === 'create' ? 'Agregar línea' : 'Editar línea' }}
        </h3>
        <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="closeLineModal()" type="button" />
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto p-6">
      <form [formGroup]="lineForm">
        <div class="flex flex-col gap-2 mb-4 pb-4 border-b border-slate-200">
          <div class="flex flex-wrap items-center gap-2">
            <span class="line-product-source-select inline-flex">
              <p-selectButton
                name="lineProductSource"
                [options]="lineProductSourceOptions"
                [ngModel]="lineProductSource()"
                [ngModelOptions]="{ standalone: true }"
                (onChange)="onLineProductSourceSelectChange($event)"
                optionLabel="label"
                optionValue="value"
                [allowEmpty]="false"
                [disabled]="lineModalMode() === 'edit'"
              >
                <ng-template pTemplate="item" let-item>
                  <span class="flex items-center justify-center gap-2">
                    @if (lineProductSource() === item.value) {
                      <i class="pi pi-check text-blue-600 text-sm shrink-0" aria-hidden="true"></i>
                    }
                    <span>{{ item.label }}</span>
                  </span>
                </ng-template>
              </p-selectButton>
            </span>
            <i
              class="pi pi-info-circle text-slate-400 cursor-help text-lg shrink-0"
              [pTooltip]="lineProductSourceTooltip"
              [escape]="false"
              tooltipPosition="bottom"
            ></i>
          </div>
          <small class="text-slate-500">
            @if (lineProductSource() === 'CATALOG') {
              Busca un producto del catálogo para precargar datos e impuestos.
            } @else {
              Describe el concepto manualmente; no se vincula a un SKU del inventario.
            }
          </small>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            class="flex flex-col gap-1 md:col-span-2 relative rounded-md transition-opacity"
            [class.opacity-60]="lineProductSource() === 'OPEN'"
            [attr.inert]="lineProductSource() === 'OPEN' ? '' : null"
          >
            @if (lineProductSource() === 'OPEN') {
              <div
                class="absolute inset-0 z-20 rounded-md cursor-not-allowed bg-transparent"
                aria-hidden="true"
                (click)="$event.preventDefault(); $event.stopPropagation()"
                (keydown)="$event.preventDefault()"
                tabindex="-1"
              ></div>
            }
            <p-floatlabel class="w-full" variant="on">
              <p-autocomplete
                #productLineRef
                id="productLine"
                formControlName="productSelection"
                [suggestions]="productOptions()"
                (completeMethod)="onProductSearch($event)"
                (onSelect)="onProductSelected($event.value)"
                optionLabel="name"
                placeholder="Busca producto (mín. 2 caracteres)"
                fluid
              />
              <label for="productLine">Producto</label>
            </p-floatlabel>
            <small class="text-slate-500">
              @if (lineProductSource() === 'CATALOG') {
                Selecciona un producto para precargar datos e impuestos.
              } @else {
                No aplica en línea abierta; el producto del catálogo está deshabilitado.
              }
            </small>
          </div>

          <div class="flex flex-col gap-1 md:col-span-2">
            <p-floatlabel class="w-full" variant="on">
              <input pInputText
                id="lineModalDescription"
                formControlName="description"
                [maxlength]="250"
                fluid
                placeholder="Descripción de la línea"
                [invalid]="(lineForm.get('description')?.invalid ?? false) && (lineForm.get('description')?.touched ?? false)"
              />
              <label for="lineModalDescription">Descripción</label>
            </p-floatlabel>
            @if (lineForm.get('description')?.hasError('required') && lineForm.get('description')?.touched) {
              <small class="text-red-600">La descripción es obligatoria.</small>
            } @else if (lineForm.get('description')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo 250 caracteres.</small>
            } @else {
              <small class="text-slate-500">Personaliza el concepto de la línea</small>
            }
          </div>

          <div class="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-inputNumber
                  id="quantity"
                  formControlName="quantity"
                  [min]="0"
                  mode="decimal"
                  locale="en-US"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="4"
                  fluid
                  (onFocus)="selectAllOnFocus($event)"
                  (mouseup)="keepSelectionOnMouseUp($event)"
                  [invalid]="(lineForm.get('quantity')?.invalid ?? false) && (lineForm.get('quantity')?.touched ?? false)"
                />
                <label for="quantity">Cantidad</label>
              </p-floatlabel>
              @if (lineForm.get('quantity')?.hasError('required') && lineForm.get('quantity')?.touched) {
                <small class="text-red-600">La cantidad es obligatoria.</small>
              }
              @if (lineForm.get('quantity')?.hasError('min')) {
                <small class="text-red-600">La cantidad debe ser mayor a 0.</small>
              }
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-inputNumber
                  id="unitPrice"
                  formControlName="unitPrice"
                  [min]="0"
                  mode="decimal"
                  locale="en-US"
                  [minFractionDigits]="2"
                  [maxFractionDigits]="4"
                  [useGrouping]="true"
                  fluid
                  class="[&_input]:text-right"
                  (onFocus)="selectAllOnFocus($event)"
                  (mouseup)="keepSelectionOnMouseUp($event)"
                  [invalid]="(lineForm.get('unitPrice')?.invalid ?? false) && (lineForm.get('unitPrice')?.touched ?? false)"
                />
                <label for="unitPrice">Precio unitario</label>
              </p-floatlabel>
              @if (lineForm.get('unitPrice')?.hasError('required') && lineForm.get('unitPrice')?.touched) {
                <small class="text-red-600">El precio es obligatorio.</small>
              }
              @if (lineForm.get('unitPrice')?.hasError('min') && lineForm.get('unitPrice')?.touched) {
                <small class="text-red-600">El precio debe ser mayor o igual a 0.</small>
              }
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-inputNumber
                  id="lineSubtotal"
                  [ngModel]="lineSubtotal()"
                  [ngModelOptions]="{standalone: true}"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  [minFractionDigits]="2"
                  [maxFractionDigits]="4"
                  fluid
                  class="[&_input]:text-right"
                  [readonly]="true"
                  [disabled]="true"
                />
                <label for="lineSubtotal">Subtotal</label>
              </p-floatlabel>
              <small class="text-slate-500">Cantidad × precio unitario.</small>
            </div>
          </div>

          <div class="md:col-span-2 pt-3 mt-1 border-t border-slate-200">
            <button
              type="button"
              class="group inline-flex items-center gap-1.5 rounded px-1 py-1 -ml-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
              (click)="lineFiscalDetailsExpanded.set(!lineFiscalDetailsExpanded())"
              [attr.aria-expanded]="lineFiscalDetailsExpanded()"
            >
              <i
                class="pi text-[0.65rem] text-slate-400 group-hover:text-slate-600"
                [class.pi-chevron-right]="!lineFiscalDetailsExpanded()"
                [class.pi-chevron-down]="lineFiscalDetailsExpanded()"
              ></i>
              <span class="font-medium text-blue-500">Personalizar datos fiscales e impuestos</span>
            </button>
          </div>

          @if (lineFiscalDetailsExpanded()) {
            <div class="md:col-span-2 -mt-1">
              <span class="text-sm font-medium text-slate-800">Datos fiscales</span>
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-autocomplete
                  id="measureUnitLine"
                  formControlName="measureUnitSelection"
                  [suggestions]="measureUnitOptions()"
                  (completeMethod)="onMeasureUnitSearch($event)"
                  (onSelect)="onMeasureUnitSelected($event.value)"
                  optionLabel="name"
                  placeholder="Busca unidad (mín. 2 caracteres)"
                  fluid
                />
                <label for="measureUnitLine">Unidad de medida</label>
              </p-floatlabel>
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-select
                  formControlName="taxExempt"
                  inputId="taxExempt"
                  [options]="taxExemptOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full"
                />
                <label for="taxExempt">Exento de impuestos</label>
              </p-floatlabel>
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-autocomplete
                  id="taxCodeLine"
                  formControlName="taxProductCodeSelection"
                  [suggestions]="taxProductCodeOptions()"
                  (completeMethod)="onTaxProductCodeSearch($event)"
                  (onSelect)="onTaxProductCodeSelected($event.value)"
                  optionLabel="taxDescription"
                  placeholder="Busca código fiscal (mín. 2 caracteres)"
                  fluid
                />
                <label for="taxCodeLine">Código fiscal</label>
              </p-floatlabel>
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-select
                  formControlName="satObjetoImpId"
                  inputId="satObjetoImpLine"
                  [options]="satObjetoImpOptions()"
                  optionLabel="description"
                  optionValue="id"
                  class="w-full"
                  filter
                />
                <label for="satObjetoImpLine">Objeto de impuesto SAT</label>
              </p-floatlabel>
            </div>

            <div class="flex flex-col justify-center">
              <div class="flex items-center gap-2">
                <p-checkbox
                  formControlName="taxInclusive"
                  inputId="taxInclusive"
                  [binary]="true"
                />
                <label for="taxInclusive" class="cursor-pointer select-none">Precio con impuestos incluidos</label>
                <i
                  class="pi pi-info-circle text-slate-400 cursor-help"
                  [pTooltip]="taxInclusiveHelpText"
                  tooltipPosition="top"
                ></i>
              </div>
            </div>
          }
        </div>
      </form>

      @if (lineFiscalDetailsExpanded()) {
        <!-- Impuestos de la línea -->
        <div class="mt-6 border-t border-slate-200 pt-4">
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-semibold text-slate-700">Impuestos de la línea</h4>
            <p-button label="Agregar impuesto" icon="pi pi-plus" [text]="true" (onClick)="editLineTaxInModal(null)" type="button" />
          </div>
          @if (lineFormTaxes().length === 0) {
            <p class="text-sm text-slate-500">No hay impuestos. Agrega o selecciona un producto.</p>
          } @else {
            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th class="text-left py-2 text-slate-600">Impuesto</th>
                    <th class="text-left py-2 text-slate-600">Tasa</th>
                    <th class="text-left py-2 text-slate-600">Monto</th>
                    <th class="text-left py-2 text-slate-600">Aplicación</th>
                    <th class="text-left py-2 text-slate-600">Tipo de cobro</th>
                    <th class="text-right py-2 text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  @for (t of lineFormTaxes(); track $index) {
                    <tr class="border-b border-slate-100">
                      <td class="py-2">{{ getTaxDisplayWithCode(t.taxCode) }}</td>
                      <td class="py-2">{{ t.taxRate  ?? '-' }}</td>
                      <td class="py-2">{{ t.taxAmount | currency:'USD':'symbol':'1.2-2' ?? '-' }}</td>
                      <td class="py-2">{{ getTaxApplicationLabel(t.applicationType!) }}</td>
                      <td class="py-2">{{ getTaxCollectionTypeLabel(t.collectionType) }}</td>
                      <td class="py-2 text-right">
                        <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" size="small" severity="secondary" (onClick)="editLineTaxInModal($index)" type="button" [pTooltip]="'Editar'" />
                        <p-button icon="pi pi-trash" [rounded]="true" [text]="true" size="small" severity="danger" (onClick)="removeLineTaxInModal($index)" type="button" [pTooltip]="'Eliminar'" />
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      }

      </div>

      <div
        class="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-slate-100 px-4 py-3"
      >
        <p-button label="Cancelar" severity="secondary" (onClick)="closeLineModal()" type="button" />
        <p-button
          [label]="lineModalMode() === 'create' ? 'Agregar' : 'Actualizar'"
          icon="pi pi-check"
          [loading]="lineSaving()"
          [disabled]="lineForm.invalid || lineSaving() || !canEditLines()"
          (onClick)="saveLine()"
          type="button"
        />
      </div>
    </div>
  </div>
}

<!-- Modal Impuesto de línea -->
@if (lineModalOpen() && lineTaxModalOpen()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black/30" (click)="closeLineTaxModal()"></div>
    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 z-50">
      <div class="flex items-center justify-between mb-4">
        <h4 class="font-semibold text-slate-900">
          {{ editingLineTaxIndex() !== null ? 'Editar impuesto' : 'Agregar impuesto' }}
        </h4>
        <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="closeLineTaxModal()" type="button" />
      </div>
      <form [formGroup]="lineTaxForm">
        <div class="grid grid-cols-1 gap-4">
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="taxSelection"
                inputId="taxSelection"
                [options]="taxOptions()"
                optionLabel="displayName"
                placeholder="Selecciona impuesto"
                class="w-full"
                [invalid]="(lineTaxForm.get('taxSelection')?.invalid ?? false) && (lineTaxForm.get('taxSelection')?.touched ?? false)"
              />
              <label for="taxSelection">Impuesto</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="applicationType"
                inputId="applicationType"
                [options]="taxApplicationOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
              />
              <label for="applicationType">Tipo de aplicación</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1">
            <label class="block text-sm font-medium text-slate-700">Tipo de cobro</label>
            <p-selectButton
              formControlName="collectionType"
              [options]="taxCollectionTypeOptions"
              optionLabel="label"
              optionValue="value"
            >
              <ng-template pTemplate="item" let-item>
                <span class="flex items-center gap-2">
                  @if (lineTaxForm.get('collectionType')?.value === item.value) {
                    <i class="pi pi-check text-sm text-blue-500"></i>
                  }
                  <span>{{ item.label }}</span>
                </span>
              </ng-template>
            </p-selectButton>
            <small class="text-slate-500">Traslado o retención.</small>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1 min-w-0">
              <p-floatlabel class="w-full" variant="on">
                <p-inputNumber
                  id="taxRateLine"
                  formControlName="taxRate"
                  [min]="0"
                  [max]="100"
                  [maxlength]="2"
                  mode="decimal"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="2"
                  [allowEmpty]="true"
                  fluid
                />
                <label for="taxRateLine">Tasa (%)</label>
              </p-floatlabel>
              <small class="text-slate-500">% de impuestos</small>
            </div>
            <div class="flex flex-col gap-1 min-w-0">
              <p-floatlabel class="w-full" variant="on">
                <p-inputNumber
                  id="taxAmountLine"
                  formControlName="taxAmount"
                  [min]="0"
                  mode="decimal"
                  inputStyleClass="text-right"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="4"
                  [allowEmpty]="true"
                  fluid
                />
                <label for="taxAmountLine">Monto</label>
              </p-floatlabel>
              <small class="text-slate-500">Cuota de impuesto</small>
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-inputNumber
                id="taxBaseLine"
                formControlName="taxBase"
                [min]="0"
                mode="decimal"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
                fluid
                placeholder="0"
              />
              <label for="taxBaseLine">Base</label>
            </p-floatlabel>
          </div>
        </div>
      </form>
      <div class="flex justify-end gap-3 mt-6">
        <p-button label="Cancelar" severity="secondary" (onClick)="closeLineTaxModal()" type="button" />
        <p-button label="Guardar" icon="pi pi-check" [disabled]="lineTaxForm.invalid" (onClick)="saveLineTaxInModal()" type="button" />
      </div>
    </div>
  </div>
}

<p-drawer
  [visible]="isCustomerDetailsOpen()"
  position="right"
  (visibleChange)="onCustomerDrawerVisibleChange($event)"
  styleClass="customer-details-drawer !w-[800px]"
  [blockScroll]="true">
  <ng-template pTemplate="header">
    <div class="flex items-center justify-between w-full gap-3">
      <div class="flex items-center gap-3 min-w-0">
        <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600 shadow-sm">
          <i class="pi pi-user text-base"></i>
        </span>
        <h3 class="text-lg font-semibold text-slate-900">Detalles del cliente</h3>
      </div>
      @if (selectedCustomer() && !customerDetailsLoading()) {
        <button
          type="button"
          class="inline-flex items-center gap-2 text-sm font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shrink-0"
          (click)="editCustomerFromModal()">
          <i class="pi pi-pencil text-sm"></i>
          Editar
        </button>
      }
    </div>
  </ng-template>
  @if (customerDetailsLoading()) {
    <div class="flex flex-col items-center justify-center py-12 gap-4">
      <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
      <p class="text-sm text-slate-600">Cargando cliente...</p>
    </div>
  } @else if (selectedCustomer()) {
    <app-customer-read
      [customer]="selectedCustomer()!"
      (openSale)="openSaleFromCustomer($event)" />
  }
</p-drawer>

<p-drawer
  [visible]="isSubscriptionDetailsOpen()"
  position="right"
  (visibleChange)="onSubscriptionDrawerVisibleChange($event)"
  styleClass="subscription-details-drawer !w-[800px]"
  [blockScroll]="true">
  <ng-template pTemplate="header">
    <div class="flex items-center justify-between w-full gap-3">
      <div class="flex items-center gap-3 min-w-0">
        <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-600 shadow-sm">
          <i class="pi pi-sync text-base"></i>
        </span>
        <h3 class="text-lg font-semibold text-slate-900">Detalle de suscripción</h3>
      </div>
      @if (selectedSubscription() && !subscriptionDetailsLoading()) {
        <button
          type="button"
          class="inline-flex items-center gap-2 text-sm font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shrink-0"
          (click)="editSubscriptionFromModal()">
          <i class="pi pi-pencil text-sm"></i>
          Editar
        </button>
      }
    </div>
  </ng-template>
  @if (subscriptionDetailsLoading()) {
    <div class="flex flex-col items-center justify-center py-12 gap-4">
      <i class="pi pi-spin pi-spinner text-4xl text-teal-500"></i>
      <p class="text-sm text-slate-600">Cargando suscripción...</p>
    </div>
  } @else if (selectedSubscription()) {
    <app-subscription-read
      [subscription]="selectedSubscription()!"
      (openSale)="openSaleFromSubscription($event)" />
  }
</p-drawer>

